import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabView, TabPanel } from "primereact/tabview";
import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useMutation, useQueries, useQuery, useQueryClient } from "react-query";
import { queryKeys } from "../../api/query";
import { Panel } from "primereact/panel";
import { usePeopleDetail } from "../../api/queries/people/usePeopleDetail";
import { useUpdatePeople } from "../../api/queries/people/useUpdatePeople";
import { useDeletePeople } from "../../api/queries/people/useDeletePeople";
import { useCreatePeople } from "../../api/queries/people/useCreatePeople";
import styles from "./styles.module.css";
import {
  AccountVisibility,
  CreatePeopleCommunityPayload,
  CreatePeopleEducationPayload,
  CreatePeopleJobPayload,
  CreatePeoplePayload,
  ImportData,
  PeopleDetailAccountVisibility,
  PeopleDetailCommunity,
  PeopleVisibility,
  UpdatePeopleEducationPayload,
  UpdatePeopleJobPayload,
  UpdatePeoplePayload,
} from "../../api/types/people";
import {
  Dropdown,
  DropdownChangeEvent,
  DropdownProps,
} from "primereact/dropdown";
import { useSkills } from "../../api/queries/skills/useSkills";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";
import { useJourneyTypes } from "../../api/queries/enum/useJourneyTypes";
import { Calendar } from "primereact/calendar";
import { Nullable } from "primereact/ts-helpers";
import moment from "moment";
import * as yup from "yup";
import { classNames } from "primereact/utils";

import { useCampusActivities } from "../../api/queries/enum/useCampusActivities";
import { useCommunityOptions } from "../../api/queries/people/useCommunityOptions";
import { useFields } from "../../api/queries/enum/useFields";
import { defaultEditorModules, Editor } from "../Editor";
import { useDebounce } from "../../common/useDebounce";
import { googleMapsSearchByTextRequest } from "../../api/queries/google-maps/search-by-text";
import { AutoComplete, AutoCompleteChangeEvent } from "primereact/autocomplete";
import commonStyles from "../common.module.css";
import { updateAccountVisibilityRequest } from "../../api/queries/account/visibility";
import { linkListRequest } from "../../api/queries/links/list";
import { Links } from "./link";
import { LinkSectionEnum } from "../../api/types/link";
import {
  createAccountVisibility,
  deleteAccountVisibility,
  getAccountVisibilities,
} from "../../api/queries/people/account-visibilities";
import { AccountDetail, AccountListItem } from "../../api/types/account";
import { Institution } from "../../api/types/institution";
import { useInstitutionsSearch } from "../../api/queries/institutions/useInstitutionsSearch";
import { useUploadProfilePicture } from "../../api/queries/people/useUploadProfilePicture";
import { loadFile } from "../../api/queries/files/loadFile";
import { deleteFile } from "../../api/queries/files/deleteFile";
import { useAppToast } from "../../context/toast";
import { accountsRequest } from "../../api/queries/account/accounts";
import { TagListItem, TagListResponseData } from "../../api/types/tag";
import { tagListRequest } from "../../api/queries/tag/list";
import { updatePeopleTagsRequest } from "../../api/queries/people/updatePeopleTags";
import debounce from "lodash.debounce";
import ImportDataViewer from "./importDataViewer";
import { approveProfile } from "../../api/queries/people/approveProfile";
import { userContext } from "../../context/user";
import {
  getPathReasonsRequest,
  PathReasonsResponseData,
} from "../../api/queries/people/getPathReasons";
import {
  getDegreesRequest,
  DegreesResponseData,
} from "../../api/queries/people/getDegrees";
import {
  getLearnerTagsRequest,
  LearnerTagsResponseData,
} from "../../api/queries/people/getLearnerTags";
import { contentBoxListRequest } from "../../api/queries/context/list";
import { ContentBoxSectionEnum } from "../../api/types/contentBox";
import { ContentBoxList } from "./ContentBox";
import { CommunityOption, CommunityValue } from "../../api/types/community";
import { useAllFieldTypes } from "../../api/queries/field-types/useAllFieldTypes";
import { useProfileFields } from "../../api/queries/profile-fields/useProfileFields";
import { ProfileFieldValue } from "../../api/types/crm/profile-fields";
import { useFieldManager } from "../../hooks/useFieldManager";
import { DynamicField } from "../../components/form/DynamicField";
import { GroupFieldSection } from "./GroupFieldSection";
import { NestedGroupRenderer } from "./NestedGroupRenderer";
import { DataTypeEnum, FieldType } from "../../api/types/field-type";
import {
  highlightSearchTerm,
  scrollToFirstHighlight,
  containsSearchTerm,
  jumpToNextHighlight,
  jumpToPreviousHighlight,
  getHighlightNavigationInfo,
  resetHighlightNavigation,
} from "../../utils/searchHighlight";
import { parseQueryString } from "../../common/utils";
import { usePersonalInfo } from "./hooks/usePersonalInfo";
import { useProfessionalInfo } from "./hooks/useProfessionalInfo";
import { useEducationState } from "./hooks/useEducationState";
import { SearchHighlightedEditor } from "./SearchHighlightedEditor";
import { FloatingSearchNavigation } from "./FloatingSearchNavigation";
import { TagChips } from "./TagChips";
import { buildTagsWithFieldValueIds } from "../../utils/tagUtils";
import {
  VisibilityOption,
  AccountVisibilityOption,
  EducationValue,
  JobValue,
  GroupedFieldItem,
  GroupedField,
  GroupedCampusActivityItem,
  GroupedCampusActivity,
  GoogleMapsSearchHometownValue,
  GoogleMapsSearchCurrentLocationValue,
} from "./form.types";

const DYNAMIC_SECTION_LOAD_DELAY_MS = 500;
const DYNAMIC_SECTION_LOADING_MIN_MS = 800;

export const fetchProfilePicture = async (
  id: string,
  setUploadedImage: (
    image: { url: string | null; id: string | null } | null,
  ) => void,
): Promise<void> => {
  try {
    const response = await loadFile(id);
    setUploadedImage({ url: response.data.presignedUrl, id });
  } catch (error) {
    setUploadedImage(null);
  }
};

export function scrollToFirstInvalidElement(): void {
  setTimeout(() => {
    const el = document.querySelector(".p-invalid");
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      if ((el as HTMLElement).focus) {
        (el as HTMLElement).focus();
      }
    }
  }, 0);
}

export function mapCommunityOption(option: any): CommunityOption {
  return {
    id: option.id,
    name: option.name,
    parent: option.parent ? mapCommunityOption(option.parent) : null,
  };
}

const normalizeFieldTypeName = (name: string): string =>
  name.toLowerCase().replace(/[^a-z0-9]/g, "");

export default function PeopleForm() {
  const { ref: appToastRef } = useAppToast();
  const { isProfileManager, isProfileManagerTemp, isAdmin } =
    useContext(userContext);
  const [communities, setCommunities] = useState<CommunityValue[]>([]);
  const deleteProfilePicture = async (
    profileId: string,
    setUploadedImage: (
      image: { url: string | null; id: string | null } | null,
    ) => void,
  ): Promise<void> => {
    try {
      await deleteFile(profileId);
      setUploadedImage(null);
      appToastRef?.current?.show({
        severity: "success",
        summary: "Profile picture deleted successfully.",
      });
    } catch (error) {
      appToastRef?.current?.show({
        severity: "error",
        summary: "Failed to delete profile picture.",
      });
    }
  };

  const approvePeople = async (profileId: string): Promise<void> => {
    try {
      await approveProfile(profileId);

      queryClient.setQueriesData(
        { queryKey: ["people-list", false] },
        (old: any) => {
          if (!old || !old.data) return old;
          const filtered = old.data.results.filter(
            (p: any) => p.id !== profileId,
          );

          return {
            ...old,
            data: {
              ...old.data,
              results: filtered,
              total_count: old.data.total_count - 1,
            },
          };
        },
      );

      queryClient.setQueriesData(
        { queryKey: ["people-list", true] },
        (old: any) => {
          if (!old || !old.data) return old;

          return {
            ...old,
            data: {
              ...old.data,
              results: old.data.results, // optionally add item here
              total_count: old.data.total_count + 1,
            },
          };
        },
      );

      appToastRef?.current?.show({
        severity: "success",
        summary: "Profile approved successfully.",
      });

      navigate("/people");
    } catch (error) {
      appToastRef?.current?.show({
        severity: "error",
        summary: "Failed to approve profile.",
      });
    }
  };
  const { uuid } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Extract search parameter from URL
  const searchParams = parseQueryString(location.search);
  const searchTerm = searchParams.search
    ? decodeURIComponent(searchParams.search)
    : "";

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [openDynamicSectionIds, setOpenDynamicSectionIds] = useState<
    Set<string>
  >(new Set());
  const [loadedDynamicSectionIds, setLoadedDynamicSectionIds] = useState<
    Set<string>
  >(new Set());
  const [loadingDynamicSectionIds, setLoadingDynamicSectionIds] = useState<
    Set<string>
  >(new Set());
  const visibilityOptions: VisibilityOption[] = [
    { value: "full", label: "Full" },
    { value: "without_contact", label: "Without contact" },
    { value: "none", label: "None" },
    { value: "full", label: "Full" },
  ];

  const accountVisibilityOptions: AccountVisibilityOption[] = [
    { value: "all", label: "All" },
    { value: "without_contact", label: "Without contact" },
    { value: "none", label: "None" },
    { value: "full", label: "Full" },
  ];

  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [selectedVisibility, setSelectedVisibility] =
    useState<AccountVisibility | null>(null);

  const { data: accountsData, isLoading: isLoadingAccounts } = useQuery({
    queryFn: () =>
      !isProfileManager() && !isProfileManagerTemp()
        ? accountsRequest({ limit: 100, offset: 0 })
        : Promise.resolve(null),
    queryKey: "accounts",
    enabled: !isProfileManager() && !isProfileManagerTemp(),
  });

  const handleAccountChange = (e: DropdownChangeEvent) => {
    setSelectedAccount(e.value);
  };

  const handleVisibilityChange = (e: DropdownChangeEvent) => {
    setSelectedVisibility(e.value);
  };

  const handleAccountVisibilitySubmit = async () => {
    if (selectedAccount && selectedVisibility && uuid) {
      const existingVisibility = newAccountVisibilities.find(
        (av) => av.account.id === selectedAccount,
      );
      if (existingVisibility) {
        await updateAccountVisibility.mutateAsync({
          id: existingVisibility.id,
          visibility: selectedVisibility,
        });
      } else {
        await createAccountVisibility(uuid, {
          account_id: selectedAccount,
          visibility: selectedVisibility,
        });
        await queryClient.invalidateQueries(
          queryKeys.peopleAccountVisibility(uuid),
        );
      }
      appToastRef?.current?.show({
        severity: "success",
        summary: "Account visibility updated successfully.",
      });
    }
  };

  const deleteAccountVisibilityMutation = useMutation({
    mutationFn: (id: string) => deleteAccountVisibility(id),
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.peopleAccountVisibility(uuid));
      appToastRef?.current?.show({
        severity: "success",
        summary: "Account visibility deleted successfully.",
      });
    },
    onError: () => {
      appToastRef?.current?.show({
        severity: "error",
        summary: "Failed to delete account visibility.",
      });
    },
  });

  const onDeleteAccountVisibility = async (id: string) => {
    // Update local state immediately for optimistic UI
    setNewAccountVisibilities((prev) => prev.filter((av) => av.id !== id));
    // Trigger the mutation
    await deleteAccountVisibilityMutation.mutateAsync(id);
  };

  const ratingOptions: Array<{ value: null | number; label: string }> = [
    { value: null, label: "Not rated" },
    { value: 1, label: "1" },
    { value: 2, label: "2" },
    { value: 3, label: "3" },
    { value: 4, label: "4" },
    { value: 5, label: "5" },
  ];

  const createPeople = useCreatePeople();
  const deletePeople = useDeletePeople();
  const updatePeople = useUpdatePeople();
  const { data: peopleResponse } = usePeopleDetail(uuid);
  const { data: skillsResponse } = useSkills();

  const [pictureFile, setPictureFile] = useState<File | null>(null);
  const uploadProfilePicture = useUploadProfilePicture();
  const [uploadedImage, setUploadedImage] = useState<{
    url: string | null;
    id: string | null;
  } | null>({ url: null, id: null });

  // --- education institution ---------------------------------------------------------------------
  const [educationInstitutionOptions, setEducationInstitutionOptions] =
    useState<Institution[]>([]);
  const [educationInstitutionName, setEducationInstitutionName] =
    useState<string>("");
  const educationInstitutionNameDebounced = useDebounce(
    educationInstitutionName,
    500,
  );

  const onEducationInstitutionNameChange = (
    index: number,
    event: AutoCompleteChangeEvent,
  ): void => {
    if (typeof event.value === "string") {
      onEducationInstitutionChange(index, {
        id: null,
        name: event.value,
      });
      setEducationInstitutionName(event.value);
    } else {
      const institution: Institution = event.value;
      onEducationInstitutionChange(index, {
        id: institution.id,
        name: institution.name,
      });
      setEducationInstitutionName(institution.name);
    }
  };

  const { data: educationInstitutionsResponse } = useInstitutionsSearch({
    offset: 0,
    limit: 5,
    search: educationInstitutionNameDebounced,
  });

  useEffect(() => {
    if (educationInstitutionsResponse) {
      setEducationInstitutionOptions(
        educationInstitutionsResponse.data.results,
      );
    }
  }, [educationInstitutionsResponse]);

  const onEducationInstitutionSearchComplete = () => {};
  const onCommunityInstitutionSearchComplete = () => {};
  // -----------------------------------------------------------------------------------------------

  // --- job institution ---------------------------------------------------------------------
  const [jobInstitutionOptions, setJobInstitutionOptions] = useState<
    Institution[]
  >([]);
  const [jobInstitutionName, setJobInstitutionName] = useState<string>("");
  const jobInstitutionNameDebounced = useDebounce(jobInstitutionName, 500);

  const onJobInstitutionNameChange = (
    index: number,
    event: AutoCompleteChangeEvent,
  ): void => {
    if (typeof event.value === "string") {
      onJobInstitutionChange(index, {
        id: null,
        name: event.value,
      });
      setJobInstitutionName(event.value);
    } else {
      const institution: Institution = event.value;
      onJobInstitutionChange(index, {
        id: institution.id,
        name: institution.name,
      });
      setJobInstitutionName(institution.name);
    }
  };

  const { data: jobInstitutionsResponse } = useInstitutionsSearch({
    offset: 0,
    limit: 5,
    search: jobInstitutionNameDebounced,
  });

  useEffect(() => {
    if (jobInstitutionsResponse) {
      setJobInstitutionOptions(jobInstitutionsResponse.data.results);
    }
  }, [jobInstitutionsResponse]);

  const onJobInstitutionSearchComplete = () => {};
  // -----------------------------------------------------------------------------------------------

  const { data: journeyTypesResponse } = useJourneyTypes();
  const { data: campusActivitiesResponse } = useCampusActivities();
  const { data: communityOptionsResponse } = useCommunityOptions();
  const groupedCommunityOptions = useMemo(() => {
    if (!communityOptionsResponse?.data?.results) return [];

    const parents = communityOptionsResponse.data.results.filter(
      (o) => !o.parent,
    );
    return parents.map((parent) => ({
      label: parent.name,
      code: parent.id,
      items: communityOptionsResponse.data.results
        .filter((o) => o.parent && o.parent.id === parent.id)
        .map((child) => ({
          label: child.name,
          value: child.id,
          data: child,
        })),
    }));
  }, [communityOptionsResponse]);

  const { data: fieldsResponse } = useFields();

  const [newAccountVisibilities, setNewAccountVisibilities] = useState<
    PeopleDetailAccountVisibility[]
  >([]);

  const { data: accountVisibilities } = useQuery({
    queryFn: () =>
      !isProfileManager() && !isProfileManagerTemp()
        ? getAccountVisibilities(uuid)
        : Promise.resolve(null),
    queryKey: queryKeys.peopleAccountVisibility(uuid),
    enabled: Boolean(uuid) && !isProfileManager(),
  });

  useEffect(() => {
    if (accountVisibilities) {
      setNewAccountVisibilities(accountVisibilities.data.results);
    }
  }, [accountVisibilities]);

  const onAccountVisibilityChange = (
    account: AccountDetail,
    visibility: AccountVisibility,
  ) => {
    setNewAccountVisibilities(
      newAccountVisibilities.map((av) => {
        if (av.account.id === account.id) {
          av.visibility = visibility;
        }

        return av;
      }),
    );
  };

  const updateAccountVisibility = useMutation({
    mutationFn: ({
      id,
      visibility,
    }: {
      id: string;
      visibility: AccountVisibility;
    }) => updateAccountVisibilityRequest(id, visibility),
  });

  const { data: profileLinks } = useQuery({
    queryFn: () => linkListRequest(LinkSectionEnum.PROFILE, uuid),
    queryKey: queryKeys.links(LinkSectionEnum.PROFILE, uuid),
    enabled: Boolean(uuid),
  });

  const { data: profileContentBoxes } = useQuery({
    queryKey: ["content-boxes", ContentBoxSectionEnum.PROFILE, uuid],
    queryFn: () => contentBoxListRequest(ContentBoxSectionEnum.PROFILE, uuid),
    enabled: Boolean(uuid),
  });

  const profileEducations = peopleResponse?.data.education || [];

  const educationQueries = useQueries(
    profileEducations.map((education) => {
      return {
        queryFn: () => linkListRequest(LinkSectionEnum.EDUCATION, education.id),
        queryKey: queryKeys.links(LinkSectionEnum.EDUCATION, education.id),
      };
    }),
  );

  const educationContentBoxQueries = useQueries(
    profileEducations.map((item) => ({
      queryKey: ["content-boxes", ContentBoxSectionEnum.EDUCATION, item.id],
      queryFn: () =>
        contentBoxListRequest(ContentBoxSectionEnum.EDUCATION, item.id),
      enabled: Boolean(item.id),
    })),
  );

  const profileCommunities = peopleResponse?.data.communities || [];
  const communityContentBoxQueries = useQueries(
    profileCommunities.map((item) => ({
      queryKey: ["content-boxes", ContentBoxSectionEnum.COMMUNITY, item.id],
      queryFn: () =>
        contentBoxListRequest(ContentBoxSectionEnum.COMMUNITY, item.id),
      enabled: Boolean(item.id),
    })),
  );

  const profileJobs = peopleResponse?.data.jobs || [];

  const jobQueries = useQueries(
    profileJobs.map((job) => {
      return {
        queryFn: () => linkListRequest(LinkSectionEnum.JOB, job.id),
        queryKey: queryKeys.links(LinkSectionEnum.JOB, job.id),
      };
    }),
  );

  const jobContentBoxQueries = useQueries(
    profileJobs.map((item) => ({
      queryKey: ["content-boxes", ContentBoxSectionEnum.CAREER, item.id],
      queryFn: () =>
        contentBoxListRequest(ContentBoxSectionEnum.CAREER, item.id),
      enabled: Boolean(item.id),
    })),
  );

  const [profileTags, setProfileTags] = useState<TagListItem[]>();
  const [tags, setTags] = useState<TagListItem[]>();
  const [approvedAt, setApprovedAt] = useState<string | null>(null);
  const [hiring, setHiring] = useState<boolean>(false);
  const [hiringNote, setHiringNote] = useState<string | null>(null);
  const [syncLinkedinData, setSyncLinkedinData] = useState<boolean>(false);
  const [pathReasons, setPathReasons] = useState<PathReasonsResponseData>([]);
  const [degrees, setDegrees] = useState<DegreesResponseData>([]);
  const [learnerTags, setLearnerTags] = useState<LearnerTagsResponseData>([]);

  useEffect(() => {
    tagListRequest().then((data: TagListResponseData) =>
      setTags(data.data.results || []),
    );
  }, []);

  useEffect(() => {
    getPathReasonsRequest().then((data: PathReasonsResponseData) =>
      setPathReasons(data),
    );
  }, []);

  useEffect(() => {
    getDegreesRequest().then((data: DegreesResponseData) => setDegrees(data));
  }, []);

  useEffect(() => {
    getLearnerTagsRequest().then((data: LearnerTagsResponseData) =>
      setLearnerTags(data),
    );
  }, []);

  const schema = yup.object({
    first_name: yup.string().required("Required field."),
    last_name: yup.string().required("Required field."),
    needs: yup.string().required("Required field."),
    bio: yup.string().required("Required field."),
    visibility: yup.string().nullable().required("Required field."),
    education: yup
      .array()
      .of(
        yup.object({
          finished: yup.boolean(),
          started_at: yup.string().required("Required field."),
          institution_id: yup.string().required("Required field."),
          journey_type_id: yup.string().required("Required field."),
          fields: yup.array().of(yup.string()).min(1, "Required field."),
        }),
      )
      .optional(),
    skills: yup.array().of(yup.string()),
    jobs: yup
      .array()
      .of(
        yup.object({
          started_at: yup.string().required("Required field."),
          institution_id: yup.string().required("Required field."),
          journey_type_id: yup.string().required("Required field."),
          position: yup.string().required("Required field."),
          field_id: yup.string().required("Required field."),
        }),
      )
      .optional(),
    hometown: yup.string(),
  });

  const [errors, setErrors] = useState<string[]>([]);

  const personalInfo = usePersonalInfo();
  const educationState = useEducationState();

  // Get all active field types for automatic field rendering
  const { data: allFieldTypes } = useAllFieldTypes(true);
  const identityFieldTypeIds = useMemo(() => {
    const ids: { firstName?: string; lastName?: string; email?: string } = {};
    if (!allFieldTypes) {
      return ids;
    }

    allFieldTypes.forEach((fieldType) => {
      if (fieldType.type === "group") {
        return;
      }

      const normalizedName = normalizeFieldTypeName(fieldType.name);
      if (normalizedName === "firstname" && !ids.firstName) {
        ids.firstName = fieldType.id;
      }
      if (normalizedName === "lastname" && !ids.lastName) {
        ids.lastName = fieldType.id;
      }
      if (normalizedName === "email" && !ids.email) {
        ids.email = fieldType.id;
      }
    });

    return ids;
  }, [allFieldTypes]);

  // Create field sections - group types become sections, non-group types become fields
  const fieldSections = useMemo(() => {
    if (!allFieldTypes) return [];

    // Get field types of type "group" (these become section headers)
    const groupFieldTypes = allFieldTypes.filter(
      (fieldType) => fieldType.type === "group",
    );

    // Get non-group field types (these become actual input fields)
    const inputFieldTypes = allFieldTypes.filter(
      (fieldType) => fieldType.type !== "group",
    );

    if (groupFieldTypes.length > 0) {
      // Create sections based on ROOT-LEVEL group field types only (no parent)
      // Nested groups will be handled recursively by NestedGroupRenderer
      const sections = groupFieldTypes
        .filter((groupFieldType) => !groupFieldType.parent) // Only root-level groups
        .map((groupFieldType) => {
          // Use the fieldTypes array from the group to get child fields
          const childFields = groupFieldType.fieldTypes || [];
          return {
            section: groupFieldType,
            fields: childFields,
          };
        })
        .filter(
          (section) =>
            section.fields.length > 0 ||
            section.section.fieldTypes?.some((ft) => ft.type === "group"),
        ); // Include sections with fields OR nested groups

      // Also add orphaned fields (fields without parent) as a separate section
      const orphanedFields = inputFieldTypes.filter(
        (fieldType) => !fieldType.parent,
      );
      if (orphanedFields.length > 0) {
        sections.push({
          section: {
            id: "uncategorized",
            name: "Other Fields",
            type: DataTypeEnum.Group,
            required: false,
            validationRules: {},
            active: true,
            allowMultiple: false,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          fields: orphanedFields,
        });
      }

      return sections;
    } else {
      // Fallback: create a single section with all input fields
      return [
        {
          section: {
            id: "all-fields",
            name: "Dynamic Fields",
            type: DataTypeEnum.Group,
            required: false,
            validationRules: {},
            active: true,
            allowMultiple: false,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          fields: inputFieldTypes,
        },
      ];
    }
  }, [allFieldTypes]);

  const { data: allProfileFieldsData } = useProfileFields(uuid);
  const profileFieldValues = useMemo(
    () => allProfileFieldsData?.fieldValues || [],
    [allProfileFieldsData?.fieldValues],
  );

  const profileFieldValuesByFieldTypeId = useMemo(() => {
    const map = new Map<string, ProfileFieldValue[]>();

    profileFieldValues.forEach((fieldValue) => {
      const values = map.get(fieldValue.fieldTypeId) || [];
      values.push(fieldValue);
      map.set(fieldValue.fieldTypeId, values);
    });

    return map;
  }, [profileFieldValues]);

  const profileFieldValuesByParentId = useMemo(() => {
    const map = new Map<string, ProfileFieldValue[]>();

    profileFieldValues.forEach((fieldValue) => {
      if (!fieldValue.parentId) {
        return;
      }

      const values = map.get(fieldValue.parentId) || [];
      values.push(fieldValue);
      map.set(fieldValue.parentId, values);
    });

    return map;
  }, [profileFieldValues]);

  const sectionFieldValuesById = useMemo(() => {
    const getAllFieldIds = (fieldType: FieldType): string[] => {
      const ids = [fieldType.id];

      fieldType.fieldTypes?.forEach((child) => {
        ids.push(...getAllFieldIds(child));
      });

      return ids;
    };

    const map = new Map<string, ProfileFieldValue[]>();

    fieldSections.forEach((section) => {
      const isGroupSection =
        section.section.type === DataTypeEnum.Group &&
        section.section.id !== "uncategorized" &&
        section.section.id !== "all-fields";
      const sectionFieldIds = new Set(
        isGroupSection
          ? getAllFieldIds(section.section)
          : section.fields.map((field) => field.id),
      );
      const primaryFieldValues = profileFieldValues.filter((fieldValue) =>
        sectionFieldIds.has(fieldValue.fieldTypeId),
      );
      const primaryFieldValueIds = new Set(
        primaryFieldValues.map((fieldValue) => fieldValue.id),
      );
      const childFieldValues = Array.from(primaryFieldValueIds).flatMap(
        (fieldValueId) => profileFieldValuesByParentId.get(fieldValueId) || [],
      );

      map.set(section.section.id, [...primaryFieldValues, ...childFieldValues]);
    });

    return map;
  }, [fieldSections, profileFieldValues, profileFieldValuesByParentId]);

  const rootFieldTypeIds = useMemo(() => {
    const ids = new Set<string>();

    allFieldTypes?.forEach((fieldType) => {
      if (fieldType.type !== DataTypeEnum.Group && !fieldType.parent) {
        ids.add(fieldType.id);
      }
    });

    return ids;
  }, [allFieldTypes]);

  // Create a map of fieldTypeId -> fieldValueId for updating existing values
  const fieldValueIds = useMemo(() => {
    const map: Record<string, string> = {};
    allProfileFieldsData?.fieldValues?.forEach((fv) => {
      // Only root-level simple fields belong to fieldManager. Group children are handled by GroupFieldSection.
      if (
        fv.type !== "group" &&
        !fv.parentId &&
        rootFieldTypeIds.has(fv.fieldTypeId)
      ) {
        map[fv.fieldTypeId] = fv.id;
      }
    });
    return map;
  }, [allProfileFieldsData?.fieldValues, rootFieldTypeIds]);

  const fieldManager = useFieldManager({
    profile_id: uuid || "",
    autoSaveDelay: 3000,
    fieldValueIds,
    onError: (errors) => {
      const errorArray = Array.isArray(errors) ? errors : [errors];

      if (errorArray.length === 1) {
        const error = errorArray[0];
        const errorMessage =
          typeof error === "object" && error?.error
            ? error.error
            : typeof error === "string"
              ? error
              : "Unknown error occurred while saving field";

        appToastRef?.current?.show({
          severity: "error",
          summary: "Field save error",
          detail: errorMessage,
          life: 5000,
        });
      } else if (errorArray.length > 1) {
        appToastRef?.current?.show({
          severity: "error",
          summary: "Multiple field save errors",
          detail: `${errorArray.length} fields failed to save. Check the form for details.`,
          life: 5000,
        });
      }
    },
    onSuccess: () => {
      appToastRef?.current?.show({
        severity: "success",
        summary: "Fields saved successfully",
        life: 3000,
      });
    },
  });

  const resolvedIdentityValues = useMemo(() => {
    const getValue = (fieldTypeId: string | undefined, fallback: string) => {
      if (!fieldTypeId) {
        return fallback;
      }

      const value = fieldManager.fields[fieldTypeId];
      return typeof value === "string" ? value : fallback;
    };

    return {
      firstName: getValue(
        identityFieldTypeIds.firstName,
        personalInfo.values.firstName,
      ),
      lastName: getValue(
        identityFieldTypeIds.lastName,
        personalInfo.values.lastName,
      ),
      email: getValue(identityFieldTypeIds.email, personalInfo.values.email),
    };
  }, [
    fieldManager.fields,
    identityFieldTypeIds.email,
    identityFieldTypeIds.firstName,
    identityFieldTypeIds.lastName,
    personalInfo.values.email,
    personalInfo.values.firstName,
    personalInfo.values.lastName,
  ]);

  const setDynamicField = fieldManager.setField;
  const setPersonalFirstName = personalInfo.setters.setFirstName;
  const setPersonalLastName = personalInfo.setters.setLastName;
  const setPersonalEmail = personalInfo.setters.setEmail;

  const handleDynamicFieldChange = useCallback(
    (fieldType: FieldType, value: any) => {
      setDynamicField(fieldType.id, value);

      if (typeof value !== "string") {
        return;
      }

      const normalizedName = normalizeFieldTypeName(fieldType.name);
      if (normalizedName === "firstname") {
        setPersonalFirstName(value);
      } else if (normalizedName === "lastname") {
        setPersonalLastName(value);
      } else if (normalizedName === "email") {
        setPersonalEmail(value);
      }
    },
    [
      setDynamicField,
      setPersonalEmail,
      setPersonalFirstName,
      setPersonalLastName,
    ],
  );

  // Handle all profile fields data for dynamic fields
  const allProcessedDataRef = useRef<string>("");

  useEffect(() => {
    if (allProfileFieldsData?.fieldValues) {
      const rootFieldValues = allProfileFieldsData.fieldValues.filter(
        (fieldValue) =>
          fieldValue.type !== "group" &&
          !fieldValue.parentId &&
          rootFieldTypeIds.has(fieldValue.fieldTypeId),
      );

      const dataKey = JSON.stringify({
        values: rootFieldValues.map((f) => ({
          fieldTypeId: f.fieldTypeId,
          id: f.id,
          value: f.value,
        })),
      });

      if (allProcessedDataRef.current !== dataKey) {
        const values: Record<string, any> = {};
        rootFieldValues.forEach((fieldValue) => {
          let processedValue = fieldValue.value;

          const fieldType = allFieldTypes?.find(
            (ft) => ft.id === fieldValue.fieldTypeId,
          );

          if (
            fieldValue.type === "list" &&
            Array.isArray(fieldValue.value) &&
            fieldValue.value.length > 0
          ) {
            if (fieldType?.allowMultiple) {
              processedValue = fieldValue.value;
            } else {
              processedValue = fieldValue.value[0];
            }
          }

          values[fieldValue.fieldTypeId] = processedValue;
        });

        fieldManager.setFields(values);

        const dynamicFirstName = identityFieldTypeIds.firstName
          ? values[identityFieldTypeIds.firstName]
          : undefined;
        if (typeof dynamicFirstName === "string") {
          personalInfo.setters.setFirstName(dynamicFirstName);
        }

        const dynamicLastName = identityFieldTypeIds.lastName
          ? values[identityFieldTypeIds.lastName]
          : undefined;
        if (typeof dynamicLastName === "string") {
          personalInfo.setters.setLastName(dynamicLastName);
        }

        const dynamicEmail = identityFieldTypeIds.email
          ? values[identityFieldTypeIds.email]
          : undefined;
        if (typeof dynamicEmail === "string") {
          personalInfo.setters.setEmail(dynamicEmail);
        }

        allProcessedDataRef.current = dataKey;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allProfileFieldsData,
    allFieldTypes,
    identityFieldTypeIds,
    personalInfo.setters.setEmail,
    personalInfo.setters.setFirstName,
    personalInfo.setters.setLastName,
    rootFieldTypeIds,
  ]); // Include allFieldTypes to re-process when field types load

  const {
    education,
    setEducation,
    handlers: educationHandlers,
  } = educationState;
  const {
    onEducationFinishedChange,
    onEducationStartedAtChange,
    onEducationFinishedAtChange,
    onEducationInstitutionChange,
    onEducationJourneyTypeIdChange,
    onEducationCampusActivitiesChange,
    onEducationFieldsChange,
    onEducationNoteChange,
    onEducationRatingChange,
    onEducationRatingNoteChange,
    onEducationPathReasonChanged,
    onEducationDegreeChanged,
    onEducationLearnerTagChanged,
  } = educationHandlers;

  const onUploadProfilePicture = async () => {
    if (!pictureFile) return;

    const payload = { picture: pictureFile };

    try {
      if (uuid) {
        const uploadFileResponse = await uploadProfilePicture.mutateAsync({
          profileId: uuid,
          ...payload,
        });
        const presignedUrlResponse = await loadFile(uploadFileResponse.data.id);
        setUploadedImage({
          id: uploadFileResponse.data.id,
          url: presignedUrlResponse.data.presignedUrl,
        });
        appToastRef?.current?.show({
          severity: "success",
          summary: "Profile picture uploaded successfully.",
        });
      }
    } catch (error) {
      appToastRef?.current?.show({
        severity: "error",
        summary: "Failed to upload profile picture.",
      });
    }
  };

  const professionalInfo = useProfessionalInfo();
  const [userNeeds, setUserNeeds] = useState<string | null>(null);
  const [userExists, setUserExists] = useState<boolean>(false);

  const onUserNeedsChange = (value: string): void => {
    const trimmedValue = value.replace(/<\/?[^>]+(>|$)/g, "").trim();
    if (trimmedValue === "") {
      setUserNeeds(null);
    } else {
      setUserNeeds(value);
    }
  };

  // --- hometown ----------------------------------------------------------------------------------
  const [hometown, setHometown] = useState<string>("");
  const hometownDebounced = useDebounce(hometown, 500);
  const [hometownCity, setHometownCity] = useState<string>("");
  const [hometownCountry, setHometownCountry] = useState<string>("");
  const [hometownOptions, setHometownOptions] = useState<
    GoogleMapsSearchHometownValue[]
  >([]);

  const onHometownSearchComplete = () => {};

  const onHometownChange = (event: AutoCompleteChangeEvent): void => {
    if (typeof event.value === "string") {
      setHometown(event.value);
      setHometownCity("");
      setHometownCountry("");
    } else {
      const hometownValue: GoogleMapsSearchHometownValue = event.value;
      setHometown(hometownValue.label);
      setHometownCity(hometownValue.city);
      setHometownCountry(hometownValue.country);
    }
  };

  const { data: hometownSearchResults } = useQuery(
    queryKeys.googleSearch(hometownDebounced),
    () => googleMapsSearchByTextRequest(hometownDebounced),
    { enabled: Boolean(hometownDebounced) },
  );

  useEffect(() => {
    if (hometownSearchResults) {
      const options = hometownSearchResults.map((item) => {
        const cityComponent = item.addressComponents?.find(
          (component) =>
            component.types.indexOf("locality") > -1 ||
            component.types.indexOf("postal_town") > -1,
        );

        const zipComponent = item.addressComponents?.find(
          (component) => component.types.indexOf("postal_code") > -1,
        );

        const stateComponent = item.addressComponents?.find(
          (component) =>
            component.types.indexOf("administrative_area_level_1") > -1,
        );

        const countryComponent = item.addressComponents?.find(
          (component) => component.types.indexOf("country") > -1,
        );

        const city = cityComponent?.longText || cityComponent?.shortText;
        const zip = zipComponent?.longText || zipComponent?.shortText;
        const state = stateComponent?.longText || stateComponent?.shortText;
        const country =
          countryComponent?.longText || countryComponent?.shortText;

        return {
          label: [city, zip, state, country].filter(Boolean).join(", "),
          country: country || "",
          city: city || "",
        };
      });

      setHometownOptions(options);
    }
  }, [hometownSearchResults]);

  // --- current location --------------------------------------------------------------------------
  const currentLocationRegex = /^([0-9]{5}|[0-9]{5}-[0-9]{4})$/;
  const [currentLocation, setCurrentLocation] = useState<string>("");
  const currentLocationDebounced = useDebounce(currentLocation, 500);
  const [currentLocationZipCode, setCurrentLocationZipCode] =
    useState<string>("");
  const [currentLocationCountry, setCurrentLocationCountry] =
    useState<string>("");
  const [currentLocationOptions, setCurrentLocationOptions] = useState<
    GoogleMapsSearchCurrentLocationValue[]
  >([]);

  const [openToChat, setOpenToChat] = useState<boolean>(false);
  const [importData, setImportData] = useState<ImportData[]>([]);
  const [showSidebar, setShowSidebar] = useState(false);

  const onCurrentLocationSearchComplete = () => {};

  const onCurrentLocationChange = async (
    event: AutoCompleteChangeEvent,
  ): Promise<void> => {
    if (typeof event.value === "string") {
      setCurrentLocation(event.value);
      setCurrentLocationCountry("");
      setCurrentLocationZipCode("");
    } else {
      const currentLocationValue: GoogleMapsSearchCurrentLocationValue =
        event.value;
      setCurrentLocation(currentLocationValue.label);
      setCurrentLocationCountry(currentLocationValue.country);
      setCurrentLocationZipCode(currentLocationValue.zip);
    }
  };

  type MutationPayload<T> = {
    mutationFn: (data: T) => Promise<any>;
    onSuccess?: () => void;
    onError?: (error: any) => void;
  };

  function useDebouncedMutation<T>({
    mutationFn,
    onSuccess,
    onError,
    delay = 1000,
  }: MutationPayload<T> & { delay?: number }) {
    const mutation = useMutation({ mutationFn, onSuccess, onError });

    const debouncedMutate = useMemo(() => {
      return debounce((data: T) => {
        mutation.mutate(data);
      }, delay);
    }, [mutation, delay]);

    return {
      debouncedMutate,
      ...mutation,
    };
  }

  const { debouncedMutate: updateProfileTags } = useDebouncedMutation<
    TagListItem[]
  >({
    mutationFn: (selectedTags) =>
      updatePeopleTagsRequest(uuid!, {
        tags: selectedTags.map((tag) => tag.id),
      }),
    onSuccess: () => {
      appToastRef?.current?.show({
        severity: "success",
        summary: "Profile tags updated!",
      });
    },
    onError: (error) => {
      appToastRef?.current?.show({
        severity: "error",
        summary: `Failed: ${error.message}`,
      });
    },
  });

  const { data: currentLocationSearchResults } = useQuery(
    queryKeys.googleSearch(currentLocationDebounced),
    () => googleMapsSearchByTextRequest(currentLocationDebounced),
    {
      enabled:
        Boolean(currentLocationDebounced) &&
        currentLocationRegex.test(currentLocationDebounced),
    },
  );

  useEffect(() => {
    if (currentLocationSearchResults) {
      const options = currentLocationSearchResults.map((item) => {
        const cityComponent = item.addressComponents?.find(
          (component) =>
            component.types.indexOf("locality") > -1 ||
            component.types.indexOf("postal_town") > -1,
        );

        const zipComponent = item.addressComponents?.find(
          (component) => component.types.indexOf("postal_code") > -1,
        );

        const stateComponent = item.addressComponents?.find(
          (component) =>
            component.types.indexOf("administrative_area_level_1") > -1,
        );

        const countryComponent = item.addressComponents?.find(
          (component) => component.types.indexOf("country") > -1,
        );

        const city = cityComponent?.longText || cityComponent?.shortText;
        const zip = zipComponent?.longText || zipComponent?.shortText;
        const state = stateComponent?.longText || stateComponent?.shortText;
        const country =
          countryComponent?.longText || countryComponent?.shortText;

        return {
          label: [city, zip, state, country].filter(Boolean).join(", "),
          country: country || "",
          zip: zip || "",
        };
      });

      setCurrentLocationOptions(options);
    } else {
      setCurrentLocationOptions([]);
    }
  }, [currentLocationSearchResults]);

  // Education state now handled by educationState hook

  const addCommunity = () => {
    setCommunities([
      ...communities,
      {
        active: true,
        time_from: null,
        time_until: null,
        institution: null,
        journey_type_id: "",
        reason_to_join: "",
        community_options: [],
      },
    ]);
  };

  const onCommunityOptionSelect = (
    communityIdx: number,
    optionId: string | null,
  ) => {
    setCommunities(
      communities.map((comm, i) =>
        i === communityIdx
          ? { ...comm, selectedCommunityOptionId: optionId }
          : comm,
      ),
    );
  };

  // New state to keep track of the pending note for each community being edited:
  const [pendingCommunityNotes, setPendingCommunityNotes] = useState<
    Record<number, string>
  >({});

  // Handler for changing the editor for the selected option (per community)
  const onPendingCommunityNoteChange = (
    communityIdx: number,
    value: string,
  ) => {
    setPendingCommunityNotes((prev) => ({
      ...prev,
      [communityIdx]: value,
    }));
  };

  // When "Add option" is clicked, add the selected option and note to community_options
  const onAddCommunityOption = (i: number) => {
    const selectedOptionId = communities[i].selectedCommunityOptionId;
    const selectedOption = communityOptionsResponse?.data?.results?.find(
      (opt) => opt.id === selectedOptionId,
    );
    const note = pendingCommunityNotes[i] || "";

    if (!selectedOptionId || !selectedOption) return;

    setCommunities(
      communities.map((comm, commIdx) => {
        if (commIdx !== i) return comm;
        return {
          ...comm,
          community_options: [
            ...comm.community_options,
            {
              id: "",
              note,
              community_option: selectedOption,
            },
          ],
          selectedCommunityOptionId: null,
        };
      }),
    );
    setPendingCommunityNotes((prev) => ({ ...prev, [i]: "" }));
  };

  const onRemoveCommunityOption = (communityIdx: number, optionId: string) => {
    setCommunities(
      communities.map((comm, i) =>
        i === communityIdx
          ? {
              ...comm,
              community_options: comm.community_options.filter(
                (opt) => opt.community_option.id !== optionId,
              ),
            }
          : comm,
      ),
    );
  };

  const deleteCommunity = (index: number) => {
    setCommunities(communities.filter((_, i) => i !== index));
  };

  const onCommunityActiveChange = (index: number, active: boolean) => {
    setCommunities(
      communities.map((item, i) => (i === index ? { ...item, active } : item)),
    );
  };

  const onCommunityTimeFromChange = (index: number, value: Nullable<Date>) => {
    setCommunities(
      communities.map((item, i) =>
        i === index ? { ...item, time_from: value } : item,
      ),
    );
  };

  const onCommunityTimeUntilChange = (index: number, value: Nullable<Date>) => {
    setCommunities(
      communities.map((item, i) =>
        i === index ? { ...item, time_until: value } : item,
      ),
    );
  };

  const [communityInstitutionOptions, setCommunityInstitutionOptions] =
    useState<Institution[]>([]);
  const [communityInstitutionName, setCommunityInstitutionName] =
    useState<string>("");
  const communityInstitutionNameDebounced = useDebounce(
    communityInstitutionName,
    500,
  );
  const { data: communityInstitutionsResponse } = useInstitutionsSearch({
    offset: 0,
    limit: 5,
    search: communityInstitutionNameDebounced,
  });

  useEffect(() => {
    if (communityInstitutionsResponse) {
      setCommunityInstitutionOptions(
        communityInstitutionsResponse.data.results,
      );
    }
  }, [communityInstitutionsResponse]);

  const onCommunityInstitutionChange = (
    index: number,
    institution: { id: string | null; name: string },
  ) => {
    setCommunities(
      communities.map((item, itemIndex) => {
        if (index === itemIndex) {
          return Object.assign({}, item, { institution });
        }

        return item;
      }),
    );
  };

  const onCommunityInstitutionNameChange = (
    index: number,
    event: AutoCompleteChangeEvent,
  ): void => {
    if (typeof event.value === "string") {
      onCommunityInstitutionChange(index, {
        id: null,
        name: event.value,
      });
      setCommunityInstitutionName(event.value);
    } else {
      // User selected from suggestions
      const institution: Institution = event.value;
      onCommunityInstitutionChange(index, {
        id: institution.id,
        name: institution.name,
      });
      setCommunityInstitutionName(institution.name);
    }
  };

  const onCommunityJourneyTypeIdChange = (
    index: number,
    journeyTypeId: string,
  ) => {
    setCommunities(
      communities.map((item, i) =>
        i === index ? { ...item, journey_type_id: journeyTypeId } : item,
      ),
    );
  };
  //

  const onCommunityReasonToJoinChange = (index: number, value: string) => {
    setCommunities((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, reason_to_join: value } : item,
      ),
    );
  };

  const onOpenToChatChange = (event: CheckboxChangeEvent) =>
    setOpenToChat(event.checked || false);

  const onHiringChange = (event: CheckboxChangeEvent) =>
    setHiring(event.checked || false);

  const onHiringNoteChange = (value: string): void => setHiringNote(value);

  const onSyncLinkedinDataChange = (event: CheckboxChangeEvent) =>
    setSyncLinkedinData(event.checked || false);

  // Education handlers are provided by useEducationState and destructured above

  const [jobs, setJobs] = useState<JobValue[]>([]);

  const addJob = () => {
    setJobs([
      ...jobs,
      {
        finished: false,
        finished_at: null,
        institution: null,
        journey_type_id: "",
        started_at: null,
        position: "",
        field_id: "",
        note: "",
        rating: null,
        rating_note: "",
        job_tags: [],
        path_reason: null,
      },
    ]);
  };

  const deleteJob = (index: number) => {
    setJobs(jobs.filter((_, i) => i !== index));
  };

  const onJobStartedAtChange = (index: number, startedAt: Nullable<Date>) => {
    setJobs(
      jobs.map((item, itemIndex) => {
        if (index === itemIndex) {
          return Object.assign({}, item, { started_at: startedAt });
        }

        return item;
      }),
    );
  };

  const onJobFinishedAtChange = (index: number, finishedAt: Nullable<Date>) => {
    setJobs(
      jobs.map((item, itemIndex) => {
        if (index === itemIndex) {
          return Object.assign({}, item, { finished_at: finishedAt });
        }

        return item;
      }),
    );
  };

  const onJobInstitutionChange = (
    index: number,
    institution: { id: string | null; name: string },
  ) => {
    setJobs(
      jobs.map((item, itemIndex) => {
        if (index === itemIndex) {
          return Object.assign({}, item, { institution });
        }

        return item;
      }),
    );
  };

  const onJobJourneyTypeIdChange = (index: number, journeyTypeId: string) => {
    setJobs(
      jobs.map((item, itemIndex) => {
        if (index === itemIndex) {
          return Object.assign({}, item, { journey_type_id: journeyTypeId });
        }

        return item;
      }),
    );
  };

  const onJobPositionChange = (index: number, position: string) => {
    setJobs(
      jobs.map((item, itemIndex) => {
        if (index === itemIndex) {
          return Object.assign({}, item, { position });
        }

        return item;
      }),
    );
  };

  const onJobFieldIdChange = (index: number, fieldId: string) => {
    setJobs(
      jobs.map((item, itemIndex) => {
        if (index === itemIndex) {
          return Object.assign({}, item, { field_id: fieldId });
        }

        return item;
      }),
    );
  };

  const onJobNoteChange = (index: number, note: string) => {
    setJobs(
      jobs.map((item, itemIndex) => {
        if (index === itemIndex) {
          return Object.assign({}, item, { note });
        }

        return item;
      }),
    );
  };

  const onJobRatingChange = (index: number, rating: number | null) => {
    setJobs(
      jobs.map((item, itemIndex) => {
        if (index === itemIndex) {
          return Object.assign({}, item, {
            rating,
          });
        }

        return item;
      }),
    );
  };

  const onJobRatingNoteChange = (index: number, rating_note: string) => {
    setJobs(
      jobs.map((item, itemIndex) => {
        if (index === itemIndex) {
          return Object.assign({}, item, {
            rating_note,
          });
        }

        return item;
      }),
    );
  };

  const onJobFinishedChange = (index: number, finished: boolean) => {
    setJobs(
      jobs.map((item, itemIndex) => {
        if (index === itemIndex) {
          return Object.assign({}, item, {
            finished,
          });
        }

        return item;
      }),
    );
  };

  const onJobPathReasonChanged = (index: number, value: string) => {
    setJobs((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, path_reason: value } : item,
      ),
    );
  };

  // Additional education attribute handlers are destructured from hook

  const onSubmit = async (event: any): Promise<void> => {
    event.preventDefault();
    setIsSubmitting(true);

    // Save any pending field changes before submitting the main form
    if (uuid) {
      const groupSavers = Array.from(groupSaveRefs.current.values());
      for (const save of groupSavers) {
        await save();
      }
      // Save regular fields
      await fieldManager.saveFields();
      await update();
    } else {
      await create();
    }
  };

  const peopleDataProcessed = useRef(false);
  const lastProcessedId = useRef<string>("");
  const groupSaveRefs = useRef<Map<string, () => Promise<void>>>(new Map());

  // Create stable callback references to avoid useEffect dependency warnings
  const settersRef = useRef({
    setFirstName: personalInfo.setters.setFirstName,
    setLastName: personalInfo.setters.setLastName,
    setEmail: personalInfo.setters.setEmail,
    setPhoneNumber: personalInfo.setters.setPhoneNumber,
    setVisibility: professionalInfo.setters.setVisibility,
    setSkills: professionalInfo.setters.setSkills,
    setNeeds: professionalInfo.setters.setNeeds,
    setBio: professionalInfo.setters.setBio,
    setEducation: setEducation,
  });

  // Update refs when setters change
  settersRef.current = {
    setFirstName: personalInfo.setters.setFirstName,
    setLastName: personalInfo.setters.setLastName,
    setEmail: personalInfo.setters.setEmail,
    setPhoneNumber: personalInfo.setters.setPhoneNumber,
    setVisibility: professionalInfo.setters.setVisibility,
    setSkills: professionalInfo.setters.setSkills,
    setNeeds: professionalInfo.setters.setNeeds,
    setBio: professionalInfo.setters.setBio,
    setEducation: setEducation,
  };

  useEffect(() => {
    // Reset processing flag when UUID changes
    if (lastProcessedId.current !== (peopleResponse?.data?.id || "")) {
      peopleDataProcessed.current = false;
      lastProcessedId.current = peopleResponse?.data?.id || "";
    }

    if (peopleResponse && !peopleDataProcessed.current) {
      const people = peopleResponse.data;

      // Handle profile picture separately to avoid state update during render
      if (people.profile_picture?.id) {
        fetchProfilePicture(people.profile_picture.id, setUploadedImage).catch(
          () => setUploadedImage(null),
        );
      } else {
        setUploadedImage(null);
      }

      settersRef.current.setFirstName(people.first_name);
      settersRef.current.setLastName(people.last_name);
      settersRef.current.setEmail(people.email || "");
      settersRef.current.setPhoneNumber(people.phone_number);
      settersRef.current.setVisibility(people.visibility);
      settersRef.current.setSkills(people.skills.map((skill) => skill.id));
      settersRef.current.setNeeds(people.needs);
      settersRef.current.setBio(people.bio);
      setUserNeeds(people.user?.needs || "");
      setUserExists(people.user !== null);
      setOpenToChat(people.open_to_chat);
      setImportData(people.import_data);
      setApprovedAt(people.approved_at);
      setHiring(people.hiring);
      setHiringNote(people.hiring_note);
      setSyncLinkedinData(people.sync_linkedin_data);

      const peopleEducation: EducationValue[] = people.education.map((item) => {
        const value: EducationValue = {
          id: item.id,
          finished: item.finished,
          institution: item.institution,
          journey_type_id: item.journey_type?.id || "",
          started_at: moment(item.started_at).toDate(),
          finished_at: item.finished_at
            ? moment(item.finished_at).toDate()
            : null,
          campus_activities: item.campus_activities.map(
            (campusActivity) => campusActivity.id,
          ),
          fields: item.fields.map((field) => field.id),
          note: item.note || "",
          rating: item.rating.rating,
          rating_note: item.rating.note || "",
          education_tags: item.education_tags,
          path_reason: item.path_reason || null,
          learner_tag: item.learner_tag || null,
          degree: item.degree || null,
        };
        return value;
      });

      setCommunities(
        (people.communities || []).map((community: PeopleDetailCommunity) => ({
          id: community.id,
          active: community.active,
          time_from: community.time_from
            ? moment(community.time_from).toDate()
            : null,
          time_until: community.time_until
            ? moment(community.time_until).toDate()
            : null,
          institution: community.institution
            ? {
                id: community.institution.id,
                name: community.institution.name,
                deleted: community.institution.deleted || false,
              }
            : null,
          journey_type_id: community.journey_type?.id || "",
          reason_to_join: community.reason_to_join ?? null,
          community_options: (community.community_options || []).map((opt) => ({
            id: opt.id,
            note: opt.note,
            community_option: mapCommunityOption(opt.community_option),
          })),
          selectedCommunityOptionId: null,
        })),
      );

      setProfileTags(people.profile_tags);
      settersRef.current.setEducation(peopleEducation);

      const peopleJobs: JobValue[] = people.jobs.map((item) => {
        const value: JobValue = {
          id: item.id,
          finished: item.finished || false,
          position: item.position,
          institution: item.institution,
          journey_type_id: item.journey_type?.id || "",
          started_at: moment(item.started_at).toDate(),
          finished_at: item.finished_at
            ? moment(item.finished_at).toDate()
            : null,
          field_id: item.field_id || "",
          note: item.note || "",
          rating: item.rating.rating,
          rating_note: item.rating.note || "",
          job_tags: item.job_tags,
          path_reason: item.path_reason || null,
        };
        return value;
      });

      setJobs(peopleJobs);
      peopleDataProcessed.current = true;
    }
  }, [peopleResponse]); // Include peopleResponse since it's used in the effect

  // State for highlight navigation
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [navigationInfo, setNavigationInfo] = useState({
    current: 0,
    total: 0,
  });

  // Update navigation info when highlights change
  const updateNavigationInfo = useCallback(() => {
    const info = getHighlightNavigationInfo(currentIndex);
    setNavigationInfo(info);
  }, [currentIndex]);

  // Scroll to first search highlight after data loads
  useEffect(() => {
    if (searchTerm && peopleResponse) {
      resetHighlightNavigation();
      setCurrentIndex(-1);
      scrollToFirstHighlight();
      setTimeout(updateNavigationInfo, 200); // Give highlights time to render
    } else {
      resetHighlightNavigation();
      setCurrentIndex(-1);
      setNavigationInfo({ current: 0, total: 0 });
    }
  }, [searchTerm, peopleResponse, updateNavigationInfo]);

  // Update navigation info when search highlights change in the DOM
  useEffect(() => {
    if (searchTerm) {
      // Create MutationObserver to watch for DOM changes
      const observer = new MutationObserver((mutations) => {
        let shouldUpdate = false;

        mutations.forEach((mutation) => {
          // Check if any added/removed nodes contain search highlights
          const checkNodes = (nodes: NodeList) => {
            for (let i = 0; i < nodes.length; i++) {
              const node = nodes[i];
              if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as Element;
                if (
                  element.matches?.("mark.search-highlight-text") ||
                  element.querySelector?.("mark.search-highlight-text")
                ) {
                  shouldUpdate = true;
                  break;
                }
              }
            }
          };

          if (mutation.addedNodes.length > 0) {
            checkNodes(mutation.addedNodes);
          }
          if (mutation.removedNodes.length > 0) {
            checkNodes(mutation.removedNodes);
          }

          // Check for class changes on existing highlight elements
          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "class" &&
            mutation.target instanceof Element &&
            mutation.target.matches("mark.search-highlight-text")
          ) {
            shouldUpdate = true;
          }
        });

        if (shouldUpdate) {
          // Small delay to ensure DOM is fully updated
          setTimeout(updateNavigationInfo, 50);
        }
      });

      // Start observing the document for changes
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class"],
      });

      return () => observer.disconnect();
    }
  }, [searchTerm, updateNavigationInfo]);

  const handleNextHighlight = () => {
    const newState = jumpToNextHighlight(currentIndex);
    if (newState) {
      setCurrentIndex(newState.currentIndex);
      setNavigationInfo({
        current: newState.currentIndex + 1,
        total: newState.total,
      });
    }
  };

  const handlePreviousHighlight = () => {
    const newState = jumpToPreviousHighlight(currentIndex);
    if (newState) {
      setCurrentIndex(newState.currentIndex);
      setNavigationInfo({
        current: newState.currentIndex + 1,
        total: newState.total,
      });
    }
  };

  const create = async () => {
    const educationPayload: CreatePeopleEducationPayload[] = education.map(
      (item) => {
        const payload: CreatePeopleEducationPayload = {
          finished: item.finished,
          institution_id: item.institution?.id as string,
          journey_type_id: item.journey_type_id,
          started_at: item.started_at
            ? moment(item.started_at).format("YYYY-MM-DD")
            : "",
          finished_at: item.finished_at
            ? moment(item.finished_at).format("YYYY-MM-DD")
            : null,
          campus_activities: item.campus_activities,
          fields: item.fields,
          note: item.note,
          rating: {
            rating: item.rating,
            note: item.rating_note || null,
          },
          path_reason: item.path_reason || null,
          learner_tag: item.learner_tag || null,
          degree: item.degree || null,
        };

        return payload;
      },
    );

    const communitiesPayload: CreatePeopleCommunityPayload[] = communities.map(
      (item) => ({
        id: item.id,
        active: item.active,
        time_from: item.time_from
          ? moment(item.time_from).format("YYYY-MM-DD")
          : null,
        time_until: item.time_until
          ? moment(item.time_until).format("YYYY-MM-DD")
          : null,
        institution_id: item.institution?.id || "",
        journey_type_id: item.journey_type_id,
        reason_to_join: item.reason_to_join,
        community_options: item.community_options.map((opt) => ({
          id: opt.community_option.id,
          note: opt.note,
        })),
      }),
    );

    const jobsPayload: CreatePeopleJobPayload[] = jobs.map((item) => {
      const payload: CreatePeopleJobPayload = {
        position: item.position,
        institution_id: item.institution?.id || "",
        journey_type_id: item.journey_type_id,
        started_at: item.started_at
          ? moment(item.started_at).format("YYYY-MM-DD")
          : "",
        finished_at: item.finished_at
          ? moment(item.finished_at).format("YYYY-MM-DD")
          : null,
        field_id: item.field_id,
        note: item.note,
        rating: {
          rating: item.rating,
          note: item.rating_note || null,
        },
        finished: item.finished,
        path_reason: item.path_reason ?? null,
      };

      return payload;
    });

    const payload: CreatePeoplePayload = {
      first_name: personalInfo.values.firstName,
      last_name: personalInfo.values.lastName,
      birth_last_name: personalInfo.values.birthLastName,
      email: personalInfo.values.email,
      needs: professionalInfo.values.needs,
      bio: professionalInfo.values.bio,
      visibility:
        professionalInfo.values.visibility || ("" as PeopleVisibility),
      education: educationPayload,
      skills: professionalInfo.values.skills,
      jobs: jobsPayload,
      hometown_city: hometownCity,
      hometown_country: hometownCountry,
      current_location_country: currentLocationCountry,
      current_location_zip_code: currentLocationZipCode,
      open_to_chat: openToChat,
      phone_number: personalInfo.values.phoneNumber,
      hiring: hiring,
      hiring_note: hiringNote,
      sync_linkedin_data: syncLinkedinData,
      community: communitiesPayload,
    };

    try {
      await schema.validate(payload, { abortEarly: false });

      await createPeople.mutateAsync(payload);

      await queryClient.invalidateQueries(queryKeys.peopleList(), {
        refetchInactive: true,
      });

      appToastRef?.current?.show({
        severity: "success",
        summary: "Profile created successfully.",
      });
      navigate("/people");
    } catch (err: any) {
      setErrors(err.inner.map((e: any) => e.path));
      scrollToFirstInvalidElement();

      appToastRef?.current?.show({
        severity: "error",
        summary: "Validation failed",
        detail: "Please check the form fields.",
        life: 10000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const update = async () => {
    if (!uuid) return;

    const educationPayload: UpdatePeopleEducationPayload[] = education.map(
      (item) => {
        const payload: UpdatePeopleEducationPayload = {
          finished: item.finished,
          institution_id: item.institution?.id || "",
          journey_type_id: item.journey_type_id,
          started_at: item.started_at
            ? moment(item.started_at).format("YYYY-MM-DD")
            : "",
          finished_at: item.finished_at
            ? moment(item.finished_at).format("YYYY-MM-DD")
            : null,
          campus_activities: item.campus_activities,
          fields: item.fields,
          note: item.note,
          rating: {
            rating: item.rating,
            note: item.rating_note || null,
          },
          path_reason: item.path_reason || null,
          learner_tag: item.learner_tag || null,
          degree: item.degree || null,
        };

        if (item.id) {
          payload.id = item.id;
        }

        return payload;
      },
    );

    const communitiesPayload = communities.map((item) => ({
      id: item.id,
      active: item.active,
      time_from: item.time_from
        ? moment(item.time_from).format("YYYY-MM-DD")
        : null,
      time_until: item.time_until
        ? moment(item.time_until).format("YYYY-MM-DD")
        : null,
      institution_id: item.institution?.id || "",
      journey_type_id: item.journey_type_id,
      reason_to_join: item.reason_to_join ?? null,
      community_options: item.community_options.map((opt) => ({
        id: opt.community_option.id,
        note: opt.note,
      })),
    }));

    const jobsPayload: UpdatePeopleJobPayload[] = jobs.map((item) => {
      const payload: UpdatePeopleJobPayload = {
        position: item.position,
        institution_id: item.institution?.id || "",
        journey_type_id: item.journey_type_id,
        started_at: item.started_at
          ? moment(item.started_at).format("YYYY-MM-DD")
          : "",
        finished_at: item.finished_at
          ? moment(item.finished_at).format("YYYY-MM-DD")
          : null,
        field_id: item.field_id,
        note: item.note,
        rating: {
          rating: item.rating,
          note: item.rating_note || null,
        },
        finished: item.finished,
        path_reason: item.path_reason ?? null,
      };

      if (item.id) {
        payload.id = item.id;
      }

      return payload;
    });

    const payload: UpdatePeoplePayload = {
      id: uuid,
      first_name: resolvedIdentityValues.firstName,
      last_name: resolvedIdentityValues.lastName,
      birth_last_name: personalInfo.values.birthLastName,
      email: resolvedIdentityValues.email,
      needs: professionalInfo.values.needs,
      bio: professionalInfo.values.bio,
      visibility:
        professionalInfo.values.visibility || ("" as PeopleVisibility),
      education: educationPayload,
      skills: professionalInfo.values.skills,
      jobs: jobsPayload,
      hometown_city: hometownCity,
      hometown_country: hometownCountry,
      current_location_country: currentLocationCountry,
      current_location_zip_code: currentLocationZipCode,
      user_needs: userNeeds === "" ? null : userNeeds,
      open_to_chat: openToChat,
      phone_number: personalInfo.values.phoneNumber,
      hiring: hiring,
      hiring_note: hiringNote,
      sync_linkedin_data: syncLinkedinData,
      community: communitiesPayload,
    };

    try {
      await schema.validate(payload, { abortEarly: false });

      await updatePeople.mutateAsync(payload);

      // Dynamic fields are now handled automatically by the field manager with auto-save

      for await (const av of newAccountVisibilities) {
        updateAccountVisibility.mutateAsync({
          id: av.id,
          visibility: av.visibility,
        });
      }

      queryClient.invalidateQueries(queryKeys.people(uuid));

      // Invalidate all profile fields queries for this profile to refresh dynamic field values
      queryClient.invalidateQueries({
        queryKey: ["profile-fields", uuid],
        exact: false,
      });

      // Also explicitly invalidate the specific query
      queryClient.invalidateQueries(queryKeys.profileFields(uuid));

      await queryClient.invalidateQueries(queryKeys.peopleList(), {
        refetchInactive: true,
      });

      appToastRef?.current?.show({
        severity: "success",
        summary: "Profile updated successfully.",
      });
      navigate("/people");
    } catch (err: any) {
      setErrors(err.inner.map((e: any) => e.path));

      scrollToFirstInvalidElement();
      if (err.inner && Array.isArray(err.inner)) {
        err.inner.forEach((e: any) => {
          appToastRef?.current?.show({
            severity: "error",
            summary: `Validation error: ${e.path}`,
            detail: e.message,
            life: 10000,
          });
        });
      }
      appToastRef?.current?.show({
        severity: "error",
        summary: "Validation failed",
        detail: "Please check the form fields.",
        life: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onDelete = async () => {
    await deletePeople.mutateAsync(uuid);

    // queryClient.invalidateQueries(queryKeys.people(uuid));
    await queryClient.invalidateQueries(queryKeys.peopleList(), {
      refetchInactive: true,
    });

    navigate("/people");
  };

  const onCancel = () => navigate("/people");

  const [careerGroupedFields, setCareerGroupedFields] = useState<
    GroupedFieldItem[]
  >([]);

  useEffect(() => {
    if (fieldsResponse) {
      const careerFields = fieldsResponse.data.results.filter(
        (field) => field.type === "career",
      );

      const levelOne = careerFields.filter((field) => field.parent === null);

      const levelTwo = careerFields.filter(
        (field) => field.parent !== null && field.parent.parent === null,
      );

      const levelThree = careerFields.filter(
        (field) => field.parent !== null && field.parent.parent !== null,
      );

      const careerGroupedFields: GroupedFieldItem[] = [];

      levelOne.forEach((levelOneField) => {
        careerGroupedFields.push({
          label: levelOneField.name,
          value: levelOneField.id,
          level: 1,
        });

        levelTwo
          .filter(
            (levelTwoField) => levelTwoField.parent?.id === levelOneField.id,
          )
          .forEach((levelTwoField) => {
            careerGroupedFields.push({
              label: levelTwoField.name,
              value: levelTwoField.id,
              level: 2,
            });

            levelThree
              .filter(
                (levelThreeField) =>
                  levelThreeField.parent?.id === levelTwoField.id,
              )
              .forEach((levelThreeField) => {
                careerGroupedFields.push({
                  label: levelThreeField.name,
                  value: levelThreeField.id,
                  level: 3,
                });
              });
          });
      });

      setCareerGroupedFields(careerGroupedFields);
    }
  }, [fieldsResponse]);

  const careerFieldValueTemplate = (
    option: GroupedFieldItem,
    props: DropdownProps,
  ) => {
    if (option) {
      return (
        <div className="flex align-items-center">
          <div>{option.label}</div>
        </div>
      );
    }

    return <div>&nbsp;</div>;
  };

  const careerFieldItemTemplate = (option: GroupedFieldItem) => {
    return (
      <div
        style={{ paddingLeft: `${option.level}rem` }}
        className={classNames({
          "font-bold": option.level === 1,
          "font-italic": option.level === 2,
        })}
      >
        <div>{option.label}</div>
      </div>
    );
  };

  const [educationGroupedFields, setEducationGroupedFields] = useState<
    GroupedField[]
  >([]);

  useEffect(() => {
    if (fieldsResponse) {
      const educationFields = fieldsResponse.data.results.filter(
        (field) => field.type === "education",
      );

      // Recursive function: get all descendants (not including the parent itself)
      const getDescendants = (
        parentId: string,
        level = 1,
      ): GroupedFieldItem[] =>
        educationFields
          .filter((f) => f.parent?.id === parentId)
          .flatMap((child) => [
            {
              label: child.name,
              value: child.id,
              level,
            },
            ...getDescendants(child.id, level + 1), // get child's children, etc.
          ]);

      // Top-level fields (parents with no parent)
      const parents = educationFields.filter((f) => !f.parent);

      // Each parent is a group, its items are only descendants (not the parent)
      const grouped: GroupedField[] = parents.map((parent) => ({
        label: parent.name,
        items: getDescendants(parent.id),
      }));

      setEducationGroupedFields(grouped);
    }
  }, [fieldsResponse]);

  const educationFieldItemTemplate = (option: GroupedFieldItem) => {
    return (
      <div style={{ paddingLeft: `${option.level * 16}px` }}>
        {option.label}
      </div>
    );
  };

  const [groupedCampusActivities, setGroupedCampusActivities] = useState<
    GroupedCampusActivity[]
  >([]);

  useEffect(() => {
    if (campusActivitiesResponse) {
      let campusActivities = campusActivitiesResponse.data.results;

      const campusActivityParents = campusActivities.filter(
        (campusActivityItem) => campusActivityItem.parent === null,
      );

      const groupedCampusActivities: GroupedCampusActivity[] = [];

      campusActivityParents.forEach((parent) => {
        groupedCampusActivities.push({
          label: parent.name,
          items: campusActivities
            .filter((campusActivity) => campusActivity.parent?.id === parent.id)
            .map((campusActivity) => ({
              label: campusActivity.name,
              value: campusActivity.id,
            })),
        });
      });

      setGroupedCampusActivities(groupedCampusActivities);
    }
  }, [campusActivitiesResponse]);

  const campusActivityItemTemplate = (option: GroupedCampusActivityItem) => {
    return (
      <div className={"pl-5"}>
        <div>{highlightSearchTerm(option.label, searchTerm)}</div>
      </div>
    );
  };

  // Helper function to check if any selected campus activities match the search term
  const containsCampusActivitySearch = (
    campusActivityIds: string[],
    searchTerm: string | null,
  ): boolean => {
    if (
      !searchTerm ||
      !campusActivityIds.length ||
      !groupedCampusActivities.length
    )
      return false;

    // Find all campus activity names from the selected IDs
    const selectedActivityNames: string[] = [];
    groupedCampusActivities.forEach((group) => {
      group.items.forEach((item) => {
        if (campusActivityIds.includes(item.value)) {
          selectedActivityNames.push(item.label);
        }
      });
    });

    // Check if any of the selected activity names contain the search term
    return selectedActivityNames.some((name) =>
      containsSearchTerm(name, searchTerm),
    );
  };

  return (
    <div className={styles["form-with-sidebar-wrapper"]}>
      <div
        style={{
          marginRight:
            importData && window.innerWidth >= 768 && activeTabIndex === 0
              ? "30rem"
              : 0,
        }}
      >
        <form onSubmit={onSubmit} className="col-12">
          {uuid && importData && approvedAt === null && isAdmin() && (
            <Panel header="Approve profile" toggleable={true} className="mb-3">
              <Button
                label="Approve profile"
                icon="pi pi-trash"
                className="ml-2"
                onClick={() => {
                  approvePeople(uuid);
                }}
              />
            </Panel>
          )}
          <TabView
            activeIndex={activeTabIndex}
            onTabChange={(e) => setActiveTabIndex(e.index)}
          >
            <TabPanel header="Legacy Profile">
              {uuid && (
                <Panel
                  header="Upload Profile Picture"
                  toggleable={true}
                  className="mb-3"
                >
                  {uploadedImage?.url && (
                    <div>
                      <img
                        src={uploadedImage.url}
                        alt="Profile"
                        style={{ maxHeight: "200px", width: "auto" }}
                      />
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      setPictureFile(e.target.files?.[0] || null)
                    }
                  />
                  <br />
                  <Button
                    label="Upload Picture"
                    className="mt-2"
                    onClick={onUploadProfilePicture}
                    disabled={!pictureFile}
                  />

                  <Button
                    label="Delete picture"
                    icon="pi pi-trash"
                    className="ml-2"
                    onClick={() => {
                      deleteProfilePicture(uuid, () => setUploadedImage(null));
                    }}
                  />
                </Panel>
              )}
              <Panel header="People" toggleable={true} className="mb-3">
                {searchTerm && (
                  <div className="mb-3 p-2 border-round surface-100">
                    <div className="flex align-items-center justify-content-between">
                      <span className="text-sm text-color-secondary">
                        Search highlights: {navigationInfo.current} of{" "}
                        {navigationInfo.total}
                      </span>
                      <div className="flex gap-2">
                        <Button
                          icon="pi pi-angle-up"
                          className="p-button-sm p-button-text"
                          onClick={handlePreviousHighlight}
                          disabled={navigationInfo.total === 0}
                          tooltip="Previous highlight"
                          type="button"
                        />
                        <Button
                          icon="pi pi-angle-down"
                          className="p-button-sm p-button-text"
                          onClick={handleNextHighlight}
                          disabled={navigationInfo.total === 0}
                          tooltip="Next highlight"
                          type="button"
                        />
                      </div>
                    </div>
                  </div>
                )}
                <div className="p-fluid">
                  <div className="grid formgrid pt-3">
                    <div className="field col-12 md:col-3 ">
                      <div className="p-float-label mt-3">
                        <InputText
                          value={personalInfo.values.firstName}
                          onChange={personalInfo.handlers.onFirstNameChange}
                          className={classNames({
                            "p-invalid": errors.indexOf("first_name") > -1,
                            "search-highlight":
                              searchTerm &&
                              containsSearchTerm(
                                personalInfo.values.firstName,
                                searchTerm,
                              ),
                          })}
                        />
                        <label>First Name *</label>
                      </div>
                      {searchTerm &&
                        containsSearchTerm(
                          personalInfo.values.firstName,
                          searchTerm,
                        ) && (
                          <small className="text-primary">
                            <i className="pi pi-check mr-1"></i>Matches search:{" "}
                            {highlightSearchTerm(
                              personalInfo.values.firstName,
                              searchTerm,
                            )}
                          </small>
                        )}
                    </div>

                    <div className="field col-12 md:col-3">
                      <div className="p-float-label mt-3">
                        <InputText
                          value={personalInfo.values.lastName}
                          onChange={personalInfo.handlers.onLastNameChange}
                          className={classNames({
                            "p-invalid": errors.indexOf("last_name") > -1,
                            "search-highlight":
                              searchTerm &&
                              containsSearchTerm(
                                personalInfo.values.lastName,
                                searchTerm,
                              ),
                          })}
                        />
                        <label>Last Name *</label>
                      </div>
                      {searchTerm &&
                        containsSearchTerm(
                          personalInfo.values.lastName,
                          searchTerm,
                        ) && (
                          <small className="text-primary">
                            <i className="pi pi-check mr-1"></i>Matches search:{" "}
                            {highlightSearchTerm(
                              personalInfo.values.lastName,
                              searchTerm,
                            )}
                          </small>
                        )}
                    </div>

                    <div className="field col-12 md:col-3">
                      <div className="p-float-label mt-3">
                        <InputText
                          value={personalInfo.values.birthLastName}
                          onChange={personalInfo.handlers.onBirthLastNameChange}
                          className={classNames({
                            "search-highlight":
                              searchTerm &&
                              containsSearchTerm(
                                personalInfo.values.birthLastName,
                                searchTerm,
                              ),
                          })}
                        />
                        <label>Birth Last Name</label>
                      </div>
                      {searchTerm &&
                        containsSearchTerm(
                          personalInfo.values.birthLastName,
                          searchTerm,
                        ) && (
                          <small className="text-primary">
                            <i className="pi pi-check mr-1"></i>Matches search:{" "}
                            {highlightSearchTerm(
                              personalInfo.values.birthLastName,
                              searchTerm,
                            )}
                          </small>
                        )}
                    </div>

                    <div className="field col-12 md:col-3">
                      <div className="p-float-label mt-3">
                        <InputText
                          value={personalInfo.values.email}
                          onChange={personalInfo.handlers.onEmailChange}
                          className={classNames({
                            "p-invalid": errors.indexOf("email") > -1,
                            "search-highlight":
                              searchTerm &&
                              containsSearchTerm(
                                personalInfo.values.email,
                                searchTerm,
                              ),
                          })}
                        />
                        <label>Email *</label>
                      </div>
                      {searchTerm &&
                        containsSearchTerm(
                          personalInfo.values.email,
                          searchTerm,
                        ) && (
                          <small className="text-primary">
                            <i className="pi pi-check mr-1"></i>Matches search:{" "}
                            {highlightSearchTerm(
                              personalInfo.values.email,
                              searchTerm,
                            )}
                          </small>
                        )}
                    </div>

                    <div className="field col-12 md:col-3">
                      <div className="p-float-label mt-3">
                        <InputText
                          value={personalInfo.values.phoneNumber}
                          onChange={personalInfo.handlers.onPhoneNumberChange}
                          className={classNames({
                            "p-invalid": errors.indexOf("phone_number") > -1,
                          })}
                        />
                        <label>Phone number</label>
                      </div>
                    </div>

                    <div className="field col-12 md:col-3">
                      <div className="p-float-label mt-3">
                        <InputText
                          value={professionalInfo.values.needs}
                          onChange={professionalInfo.handlers.onNeedsChange}
                          className={classNames({
                            "p-invalid": errors.indexOf("needs") > -1,
                            "search-highlight":
                              searchTerm &&
                              containsSearchTerm(
                                professionalInfo.values.needs,
                                searchTerm,
                              ),
                          })}
                        />
                        <label>Needs *</label>
                      </div>
                      {searchTerm &&
                        containsSearchTerm(
                          professionalInfo.values.needs,
                          searchTerm,
                        ) && (
                          <small className="text-primary">
                            <i className="pi pi-check mr-1"></i>Matches search:{" "}
                            {highlightSearchTerm(
                              professionalInfo.values.needs,
                              searchTerm,
                            )}
                          </small>
                        )}
                    </div>

                    <div className="field col-12 md:col-3">
                      <div className="p-float-label mt-3">
                        <Dropdown
                          value={professionalInfo.values.visibility}
                          optionLabel="label"
                          optionValue="value"
                          onChange={
                            professionalInfo.handlers.onVisibilityChange
                          }
                          options={visibilityOptions}
                          className={classNames({
                            "p-invalid": errors.indexOf("visibility") > -1,
                          })}
                          filter={true}
                          filterBy="label"
                        />
                        <label>Visibility *</label>
                      </div>
                    </div>

                    <div className="field col-12 md:col-3">
                      <div className="p-float-label mt-3">
                        <MultiSelect
                          value={professionalInfo.values.skills}
                          optionLabel="name"
                          optionValue="id"
                          onChange={professionalInfo.handlers.onSkillsChange}
                          options={skillsResponse?.data.results || []}
                          className={classNames({
                            "p-invalid": errors.indexOf("skills") > -1,
                          })}
                          filter={true}
                          filterBy="name"
                          display="chip"
                          maxSelectedLabels={3}
                        />
                        <label>Skills</label>
                      </div>
                    </div>

                    <div className="field col-12 md:col-3">
                      <div className="p-float-label mt-3">
                        <AutoComplete
                          value={hometown}
                          suggestions={hometownOptions}
                          completeMethod={onHometownSearchComplete}
                          onChange={onHometownChange}
                          field="label"
                          className={classNames({
                            "p-invalid": errors.indexOf("hometown") > -1,
                          })}
                        />
                        <label>Hometown</label>
                      </div>
                    </div>

                    <div className="field col-12 md:col-3">
                      <div className="p-float-label mt-3">
                        <AutoComplete
                          value={currentLocation}
                          suggestions={currentLocationOptions}
                          completeMethod={onCurrentLocationSearchComplete}
                          onChange={onCurrentLocationChange}
                          field="label"
                          className={classNames({
                            "p-invalid":
                              errors.indexOf("current_location") > -1,
                          })}
                          delay={500}
                          showEmptyMessage={true}
                        />
                        <label>Current ZIP Code</label>
                      </div>
                      <small className={commonStyles.fieldHelp}>
                        Use format 12345 or 12345-6789.
                      </small>
                    </div>

                    <div className="field col-12 field-checkbox md:col-3">
                      <Checkbox
                        checked={openToChat}
                        onChange={(event: CheckboxChangeEvent) =>
                          onOpenToChatChange(event)
                        }
                        inputId="open-to-chat"
                      />
                      <label htmlFor="open-to-chat">Open to chat</label>
                    </div>

                    {uuid && (
                      <div className="field col-12 md:col-3">
                        <div className="p-float-label mt-3">
                          <MultiSelect
                            value={profileTags}
                            onChange={(e) => {
                              setProfileTags(e.value);
                              updateProfileTags(e.value);
                            }}
                            options={tags}
                            optionLabel="name"
                            display="chip"
                            placeholder="Select Tags"
                            maxSelectedLabels={3}
                            className="w-full"
                            filter={true}
                            filterBy="name"
                          />
                        </div>
                      </div>
                    )}

                    <div className="field col-12 field-checkbox md:col-3">
                      <Checkbox
                        checked={hiring}
                        onChange={(event: CheckboxChangeEvent) =>
                          onHiringChange(event)
                        }
                        inputId="hiring"
                      />
                      <label htmlFor="hiring">Hiring</label>
                    </div>

                    {uuid && (
                      <div className="field col-12 field-checkbox md:col-3">
                        <Checkbox
                          checked={syncLinkedinData}
                          onChange={(event: CheckboxChangeEvent) =>
                            onSyncLinkedinDataChange(event)
                          }
                          inputId="sync-linkedin-data"
                        />
                        <label htmlFor="sync-linkedin-data">
                          Sync LinkedIn data
                        </label>
                      </div>
                    )}

                    <div className="field col-12">
                      <div className="mt-3">
                        <div className="p-panel-˚header">
                          <span className="p-panel-title">Hiring note</span>
                          <span className="p-panel-icons"></span>
                        </div>
                        <Editor
                          content={hiringNote ?? ""}
                          onChange={onHiringNoteChange}
                          modules={defaultEditorModules}
                          placeholder="Hiring note"
                        />
                      </div>
                    </div>

                    <div className="field col-12">
                      <div className="mt-3">
                        <div className="p-panel-header">
                          <span className="p-panel-title">Bio*</span>
                          <span className="p-panel-icons"></span>
                        </div>
                        {(!professionalInfo.values.bio ||
                          professionalInfo.values.bio.length === 0) &&
                          errors.indexOf("bio") > -1 && (
                            <i className="p-error">No bio provided.</i>
                          )}

                        <SearchHighlightedEditor
                          content={professionalInfo.values.bio}
                          onChange={professionalInfo.handlers.onBioChange}
                          modules={defaultEditorModules}
                          placeholder="Bio *"
                          searchTerm={searchTerm}
                          className={classNames({
                            "p-invalid": errors.indexOf("bio") > -1,
                          })}
                        />
                      </div>
                    </div>

                    <Button
                      type="button"
                      label="Show Imported Data"
                      onClick={() => setShowSidebar(true)}
                      className={`${styles["mobile-only-button"]} p-button-sm mb-2`}
                    />

                    {importData &&
                      (showSidebar || window.innerWidth >= 768) && (
                        <div
                          style={{
                            position: "fixed",
                            top: 0,
                            right: 0,
                            height: "100vh",
                            width: window.innerWidth < 768 ? "18rem" : "30rem",
                            overflowY: "auto",
                            padding: "1rem",
                            backgroundColor: "#fff",
                            borderLeft: "1px solid #ccc",
                            zIndex: 1000,
                          }}
                        >
                          <Button
                            type="button"
                            icon="pi pi-times"
                            className={`${styles["mobile-only-button"]} p-button-text p-button-sm`}
                            onClick={() => setShowSidebar(false)}
                          />
                          <h2>Imported Data from Tally</h2>
                          <ImportDataViewer importData={importData} />
                        </div>
                      )}

                    <div className="field col-12">
                      <div className="mt-3">
                        {!userExists ? (
                          <i className="text-red-500">
                            No user connected to this profile.
                          </i>
                        ) : (
                          <>
                            {userNeeds !== null &&
                              userNeeds.length === 0 &&
                              errors.indexOf("userNeeds") > -1 && (
                                <i className="text-red-500">No user needs.</i>
                              )}
                            <Editor
                              content={userNeeds ?? ""}
                              onChange={onUserNeedsChange}
                              modules={defaultEditorModules}
                              placeholder="User needs"
                            />
                          </>
                        )}
                      </div>
                    </div>

                    {uuid && (
                      <div className="field col-12">
                        <Links
                          section={LinkSectionEnum.PROFILE}
                          uuid={uuid}
                          links={profileLinks?.data.results || []}
                          availableTags={tags || []}
                        />
                      </div>
                    )}
                  </div>

                  {uuid && (
                    <ContentBoxList
                      uuid={uuid}
                      section={ContentBoxSectionEnum.PROFILE}
                      contentBoxes={profileContentBoxes?.data?.results || []}
                      availableTags={tags || []}
                    />
                  )}
                </div>
              </Panel>

              <Panel
                header="Education"
                toggleable={true}
                className="mb-3"
                headerTemplate={() => (
                  <div className="p-panel-header">
                    <span className="p-panel-title">Education</span>
                    <span className="p-panel-icons">
                      <Button
                        className="p-button-text p-button-secondary p-button-sm"
                        label="Add new"
                        icon="pi pi-plus"
                        onClick={educationState.addEducation}
                        type="button"
                      />
                    </span>
                  </div>
                )}
              >
                <div className="p-fluid">
                  {education && education.length === 0 && (
                    <div>
                      {errors.includes("education") && (
                        <span
                          className="p-invalid"
                          style={{ position: "absolute", height: 0 }}
                        ></span>
                      )}
                      <i className="text-red-500">No educations.</i>
                    </div>
                  )}

                  {education.map((item, i) => (
                    <React.Fragment key={i}>
                      <div key={i} className="grid formgrid pt-3">
                        <div className="col-12">
                          <div className="flex align-items-center mb-3">
                            <strong className="mr-3">Education #{i + 1}</strong>
                            <Button
                              type="button"
                              label="Remove"
                              icon="pi pi-trash"
                              size="small"
                              outlined={true}
                              severity="danger"
                              className="w-auto"
                              onClick={() => educationState.deleteEducation(i)}
                            />
                          </div>
                        </div>

                        <div className="field col-12 md:col-3 ">
                          <div className="p-float-label mt-3">
                            <Calendar
                              value={item.started_at}
                              onChange={(event: any) =>
                                onEducationStartedAtChange(
                                  i,
                                  event.value || null,
                                )
                              }
                              className={classNames({
                                "p-invalid":
                                  errors.indexOf(`education[${i}].started_at`) >
                                  -1,
                              })}
                            />
                            <label>Started at *</label>
                          </div>
                        </div>

                        <div className="field col-12 md:col-3 ">
                          <div className="p-float-label mt-3">
                            <Calendar
                              value={item.finished_at}
                              onChange={(event: any) =>
                                onEducationFinishedAtChange(
                                  i,
                                  event.value || null,
                                )
                              }
                              className={classNames({
                                "p-invalid":
                                  errors.indexOf(
                                    `education[${i}].finished_at`,
                                  ) > -1,
                              })}
                            />
                            <label>Finished at</label>
                          </div>
                        </div>

                        <div className="field col-12 md:col-3">
                          <div className="p-float-label mt-3">
                            <AutoComplete
                              value={item.institution?.name}
                              suggestions={educationInstitutionOptions}
                              completeMethod={
                                onEducationInstitutionSearchComplete
                              }
                              onChange={(event: AutoCompleteChangeEvent) => {
                                onEducationInstitutionNameChange(i, event);
                              }}
                              field="name"
                              className={classNames({
                                "p-invalid":
                                  errors.indexOf(
                                    `education[${i}].institution_id`,
                                  ) > -1,
                                "search-highlight":
                                  searchTerm &&
                                  containsSearchTerm(
                                    item.institution?.name,
                                    searchTerm,
                                  ),
                              })}
                            />
                            <label>Institution *</label>
                          </div>
                          {searchTerm &&
                            containsSearchTerm(
                              item.institution?.name,
                              searchTerm,
                            ) && (
                              <small className="text-primary">
                                <i className="pi pi-check mr-1"></i>Matches
                                search:{" "}
                                {highlightSearchTerm(
                                  item.institution?.name || "",
                                  searchTerm,
                                )}
                              </small>
                            )}
                          {item.institution?.deleted && (
                            <small className="p-error flex align-items-center">
                              <i className="pi pi-exclamation-triangle p-mr-2" />
                              This institution has been deleted
                            </small>
                          )}
                        </div>

                        <div className="field col-12 md:col-3">
                          <div className="p-float-label mt-3">
                            <Dropdown
                              value={item.journey_type_id}
                              onChange={(event: DropdownChangeEvent) =>
                                onEducationJourneyTypeIdChange(i, event.value)
                              }
                              options={journeyTypesResponse?.data.results || []}
                              optionLabel="name"
                              optionValue="id"
                              className={classNames({
                                "p-invalid":
                                  errors.indexOf(
                                    `education[${i}].journey_type_id`,
                                  ) > -1,
                              })}
                              filter={true}
                              filterBy="name"
                            />
                            <label>Context *</label>
                          </div>
                        </div>

                        <div className="field col-12 md:col-3">
                          <div className="p-float-label mt-3">
                            <MultiSelect
                              value={item.campus_activities}
                              onChange={(event: MultiSelectChangeEvent) =>
                                onEducationCampusActivitiesChange(
                                  i,
                                  event.value,
                                )
                              }
                              options={groupedCampusActivities || []}
                              optionLabel="label"
                              optionValue="value"
                              optionGroupLabel="label"
                              optionGroupChildren="items"
                              itemTemplate={campusActivityItemTemplate}
                              className={classNames({
                                "p-invalid":
                                  errors.indexOf(
                                    `education[${i}].campus_activities`,
                                  ) > -1,
                                "search-highlight":
                                  searchTerm &&
                                  containsCampusActivitySearch(
                                    item.campus_activities,
                                    searchTerm,
                                  ),
                              })}
                              filter={true}
                              filterBy="label"
                              display="chip"
                              maxSelectedLabels={3}
                            />
                            <label>Campus activity</label>
                          </div>
                          {searchTerm &&
                            containsCampusActivitySearch(
                              item.campus_activities,
                              searchTerm,
                            ) && (
                              <small className="text-primary">
                                <i className="pi pi-check mr-1"></i>Matches
                                search in selected campus activities
                              </small>
                            )}
                        </div>

                        <div className="field-checkbox col-12 md:col-3 ">
                          <Checkbox
                            checked={item.finished}
                            onChange={(event: CheckboxChangeEvent) =>
                              onEducationFinishedChange(
                                i,
                                event.checked || false,
                              )
                            }
                            inputId={`finished-${i}`}
                          />
                          <label htmlFor={`finished-${i}`}>Finished</label>
                        </div>

                        <div className="field col-12 md:col-3">
                          <div className="p-float-label mt-3">
                            <MultiSelect
                              value={item.fields}
                              onChange={(event: MultiSelectChangeEvent) =>
                                onEducationFieldsChange(i, event.value)
                              }
                              options={educationGroupedFields || []}
                              optionLabel="label"
                              optionValue="value"
                              optionGroupLabel="label"
                              optionGroupChildren="items"
                              itemTemplate={educationFieldItemTemplate}
                              className={classNames({
                                "p-invalid":
                                  errors.indexOf(`education[${i}].fields`) > -1,
                              })}
                              filter={true}
                              filterBy="label"
                              display="chip"
                              maxSelectedLabels={3}
                            />
                            <label>Education field *</label>
                          </div>
                        </div>

                        <div className="field col-12 md:col-3">
                          <div className="p-float-label mt-3">
                            <Dropdown
                              options={ratingOptions}
                              value={item.rating}
                              onChange={(event: DropdownChangeEvent) => {
                                onEducationRatingChange(i, event.value);
                              }}
                            />
                            <label>Rating</label>
                          </div>
                        </div>

                        <div className="field col-12 md:col-3">
                          <div className="p-float-label mt-3">
                            <InputText
                              value={item.rating_note}
                              onChange={(
                                event: ChangeEvent<HTMLInputElement>,
                              ) => {
                                onEducationRatingNoteChange(
                                  i,
                                  event.target.value,
                                );
                              }}
                            />
                            <label>Rating note</label>
                          </div>
                        </div>

                        <div className="field col-12 md:col-3">
                          <div className="p-float-label mt-3">
                            <Dropdown
                              options={pathReasons}
                              value={item.path_reason ?? null}
                              onChange={(event: DropdownChangeEvent) =>
                                onEducationPathReasonChanged(i, event.value)
                              }
                              placeholder="Select path reason"
                              filter={true}
                              filterBy="name"
                            />
                            <label>Path reason</label>
                          </div>
                        </div>

                        <div className="field col-12 md:col-3">
                          <div className="p-float-label mt-3">
                            <Dropdown
                              options={learnerTags}
                              value={item.learner_tag ?? null}
                              onChange={(event: DropdownChangeEvent) =>
                                onEducationLearnerTagChanged(i, event.value)
                              }
                              placeholder="Select learner tag"
                            />
                            <label>Learner tag</label>
                          </div>
                        </div>

                        <div className="field col-12 md:col-3">
                          <div className="p-float-label mt-3">
                            <Dropdown
                              options={degrees}
                              value={item.degree ?? null}
                              onChange={(event: DropdownChangeEvent) =>
                                onEducationDegreeChanged(i, event.value)
                              }
                              placeholder="Select degree"
                              filter={true}
                              filterBy="name"
                            />
                            <label>Degrees</label>
                          </div>
                        </div>

                        <div className="field col-12">
                          <div className="mt-3">
                            <SearchHighlightedEditor
                              content={item.note}
                              onChange={(value) =>
                                onEducationNoteChange(i, value)
                              }
                              modules={defaultEditorModules}
                              placeholder="Note"
                              searchTerm={searchTerm}
                            />
                          </div>
                        </div>
                      </div>

                      {uuid && item.id && (
                        <>
                          <Links
                            section={LinkSectionEnum.EDUCATION}
                            uuid={item.id}
                            links={
                              educationQueries?.[i]?.data?.data.results || []
                            }
                            availableTags={tags || []}
                          />

                          <ContentBoxList
                            section={ContentBoxSectionEnum.EDUCATION}
                            uuid={item.id}
                            contentBoxes={
                              educationContentBoxQueries?.[i]?.data?.data
                                .results || []
                            }
                            availableTags={tags || []}
                          />
                        </>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </Panel>

              <Panel
                header="Careers"
                toggleable={true}
                className="mb-3"
                headerTemplate={() => (
                  <div className="p-panel-header">
                    <span className="p-panel-title">Careers</span>
                    <span className="p-panel-icons">
                      <Button
                        className="p-button-text p-button-secondary p-button-sm"
                        label="Add new"
                        icon="pi pi-plus"
                        onClick={addJob}
                        type="button"
                      />
                    </span>
                  </div>
                )}
              >
                <div className="p-fluid">
                  {jobs.length === 0 && <i>No careers.</i>}

                  {jobs.map((item, i) => (
                    <React.Fragment key={i}>
                      <div key={i} className="grid formgrid pt-3">
                        <div className="col-12">
                          <div className="flex align-items-center mb-3">
                            <strong className="mr-3">Career #{i + 1}</strong>
                            <Button
                              type="button"
                              label="Remove"
                              icon="pi pi-trash"
                              size="small"
                              outlined={true}
                              severity="danger"
                              className="w-auto"
                              onClick={() => deleteJob(i)}
                            />
                          </div>
                        </div>

                        <div className="field col-12 md:col-3 ">
                          <div className="p-float-label mt-3">
                            <InputText
                              value={item.position}
                              onChange={(
                                event: ChangeEvent<HTMLInputElement>,
                              ) => onJobPositionChange(i, event.target.value)}
                              className={classNames({
                                "p-invalid":
                                  errors.indexOf(`jobs[${i}].position`) > -1,
                                "search-highlight":
                                  searchTerm &&
                                  containsSearchTerm(item.position, searchTerm),
                              })}
                            />
                            <label>Position *</label>
                          </div>
                          {searchTerm &&
                            containsSearchTerm(item.position, searchTerm) && (
                              <small className="text-primary">
                                <i className="pi pi-check mr-1"></i>Matches
                                search:{" "}
                                {highlightSearchTerm(item.position, searchTerm)}
                              </small>
                            )}
                        </div>

                        <div className="field col-12 md:col-3 ">
                          <div className="p-float-label mt-3">
                            <Calendar
                              value={item.started_at}
                              onChange={(event: any) =>
                                onJobStartedAtChange(i, event.value || null)
                              }
                              className={classNames({
                                "p-invalid":
                                  errors.indexOf(`jobs[${i}].started_at`) > -1,
                              })}
                            />
                            <label>Started at *</label>
                          </div>
                        </div>

                        <div className="field col-12 md:col-3 ">
                          <div className="p-float-label mt-3">
                            <Calendar
                              value={item.finished_at}
                              onChange={(event: any) =>
                                onJobFinishedAtChange(i, event.value || null)
                              }
                              className={classNames({
                                "p-invalid":
                                  errors.indexOf(`jobs[${i}].finished_at`) > -1,
                              })}
                            />
                            <label>Finished at</label>
                          </div>
                        </div>

                        <div className="field col-12 md:col-3">
                          <div className="p-float-label mt-3">
                            <AutoComplete
                              value={item.institution?.name}
                              suggestions={jobInstitutionOptions}
                              completeMethod={onJobInstitutionSearchComplete}
                              onChange={(event: AutoCompleteChangeEvent) => {
                                onJobInstitutionNameChange(i, event);
                              }}
                              field="name"
                              className={classNames({
                                "p-invalid":
                                  errors.indexOf(`jobs[${i}].institution_id`) >
                                  -1,
                                "search-highlight":
                                  searchTerm &&
                                  containsSearchTerm(
                                    item.institution?.name,
                                    searchTerm,
                                  ),
                              })}
                            />
                            <label>Institution *</label>
                          </div>
                          {searchTerm &&
                            containsSearchTerm(
                              item.institution?.name,
                              searchTerm,
                            ) && (
                              <small className="text-primary">
                                <i className="pi pi-check mr-1"></i>Matches
                                search:{" "}
                                {highlightSearchTerm(
                                  item.institution?.name || "",
                                  searchTerm,
                                )}
                              </small>
                            )}
                          {item.institution?.deleted && (
                            <small className="p-error flex align-items-center">
                              <i className="pi pi-exclamation-triangle p-mr-2" />
                              This institution has been deleted
                            </small>
                          )}
                        </div>

                        <div className="field col-12 md:col-3">
                          <div className="p-float-label mt-3">
                            <Dropdown
                              value={item.journey_type_id}
                              onChange={(event: DropdownChangeEvent) =>
                                onJobJourneyTypeIdChange(i, event.value)
                              }
                              options={journeyTypesResponse?.data.results || []}
                              optionLabel="name"
                              optionValue="id"
                              className={classNames({
                                "p-invalid":
                                  errors.indexOf(`jobs[${i}].journey_type_id`) >
                                  -1,
                              })}
                              filter={true}
                              filterBy="name"
                            />
                            <label>Context *</label>
                          </div>
                        </div>

                        <div className="field col-12 md:col-3">
                          <div className="p-float-label mt-3">
                            <Dropdown
                              value={item.field_id}
                              onChange={(event: DropdownChangeEvent) =>
                                onJobFieldIdChange(i, event.value)
                              }
                              options={careerGroupedFields || []}
                              optionLabel="label"
                              optionValue="value"
                              // optionGroupLabel="label"
                              // optionGroupChildren="items"
                              itemTemplate={careerFieldItemTemplate}
                              valueTemplate={careerFieldValueTemplate}
                              className={classNames({
                                "p-invalid":
                                  errors.indexOf(`jobs[${i}].field_id`) > -1,
                              })}
                              filter={true}
                              filterBy="label"
                            />
                            <label>Career field *</label>
                          </div>
                        </div>

                        <div className="field col-12 md:col-3">
                          <div className="p-float-label mt-3">
                            <Dropdown
                              options={ratingOptions}
                              value={item.rating}
                              onChange={(event: DropdownChangeEvent) => {
                                onJobRatingChange(i, event.value);
                              }}
                            />
                            <label>Rating</label>
                          </div>
                        </div>

                        <div className="field col-12 md:col-3">
                          <div className="p-float-label mt-3">
                            <InputText
                              value={item.rating_note}
                              onChange={(
                                event: ChangeEvent<HTMLInputElement>,
                              ) => {
                                onJobRatingNoteChange(i, event.target.value);
                              }}
                            />
                            <label>Rating note</label>
                          </div>
                        </div>

                        <div className="field-checkbox col-12 md:col-3 ">
                          <Checkbox
                            checked={item.finished}
                            onChange={(event: CheckboxChangeEvent) =>
                              onJobFinishedChange(i, event.checked || false)
                            }
                            inputId={`job-finished-${i}`}
                          />
                          <label htmlFor={`job-finished-${i}`}>Finished</label>
                        </div>

                        <div className="field col-12 md:col-3">
                          <div className="p-float-label mt-3">
                            <Dropdown
                              options={pathReasons}
                              value={item.path_reason ?? null}
                              onChange={(event: DropdownChangeEvent) =>
                                onJobPathReasonChanged(i, event.value)
                              }
                              placeholder="Select path reason"
                              filter={true}
                              filterBy="name"
                            />
                            <label>Path reason</label>
                          </div>
                        </div>
                        <div className="field col-12">
                          <div className="mt-3">
                            <SearchHighlightedEditor
                              content={item.note}
                              onChange={(value) => onJobNoteChange(i, value)}
                              modules={defaultEditorModules}
                              placeholder="Note"
                              searchTerm={searchTerm}
                            />
                          </div>
                        </div>
                      </div>

                      {uuid && item.id && (
                        <>
                          <Links
                            section={LinkSectionEnum.JOB}
                            uuid={item.id as string}
                            links={jobQueries?.[i]?.data?.data.results || []}
                            availableTags={tags || []}
                          />
                          <ContentBoxList
                            section={ContentBoxSectionEnum.CAREER}
                            uuid={item.id}
                            contentBoxes={
                              jobContentBoxQueries?.[i]?.data?.data.results ||
                              []
                            }
                            availableTags={tags || []}
                          />
                        </>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </Panel>

              <Panel
                header="Community"
                toggleable={true}
                className="mb-3"
                headerTemplate={() => (
                  <div className="p-panel-header">
                    <span className="p-panel-title">Community</span>
                    <span className="p-panel-icons">
                      <Button
                        className="p-button-text p-button-secondary p-button-sm"
                        label="Add new"
                        icon="pi pi-plus"
                        onClick={addCommunity}
                        type="button"
                      />
                    </span>
                  </div>
                )}
              >
                <div className="p-fluid">
                  {communities.length === 0 && <i>No communities.</i>}
                  {communities.map((item, i) => (
                    <div key={i} className="grid formgrid pt-3">
                      <div className="col-12">
                        <div className="flex align-items-center mb-3">
                          <strong className="mr-3">Community #{i + 1}</strong>
                          <Button
                            type="button"
                            label="Remove"
                            icon="pi pi-trash"
                            size="small"
                            outlined={true}
                            severity="danger"
                            className="w-auto"
                            onClick={() => deleteCommunity(i)}
                          />
                        </div>
                      </div>

                      <div className="field col-12 md:col-3">
                        <div className="p-float-label mt-3">
                          <Calendar
                            value={item.time_from}
                            onChange={(event: any) =>
                              onCommunityTimeFromChange(i, event.value || null)
                            }
                          />
                          <label>Time from</label>
                        </div>
                      </div>
                      <div className="field col-12 md:col-3">
                        <div className="p-float-label mt-3">
                          <Calendar
                            value={item.time_until}
                            onChange={(event: any) =>
                              onCommunityTimeUntilChange(i, event.value || null)
                            }
                          />
                          <label>Time until</label>
                        </div>
                      </div>

                      <div className="field col-12 md:col-3">
                        <div className="p-float-label mt-3">
                          <AutoComplete
                            value={item.institution?.name || ""}
                            suggestions={communityInstitutionOptions}
                            completeMethod={
                              onCommunityInstitutionSearchComplete
                            }
                            onChange={(event: AutoCompleteChangeEvent) =>
                              onCommunityInstitutionNameChange(i, event)
                            }
                            field="name"
                          />
                          <label>Institution</label>
                        </div>
                        {item.institution?.deleted && (
                          <small className="p-error flex align-items-center">
                            <i className="pi pi-exclamation-triangle p-mr-2" />
                            This institution has been deleted
                          </small>
                        )}
                      </div>

                      <div className="field col-12 md:col-3">
                        <div className="p-float-label mt-3">
                          <Dropdown
                            value={item.journey_type_id}
                            onChange={(event: DropdownChangeEvent) =>
                              onCommunityJourneyTypeIdChange(i, event.value)
                            }
                            options={journeyTypesResponse?.data.results || []}
                            optionLabel="name"
                            optionValue="id"
                            filter={true}
                            filterBy="name"
                          />
                          <label>Context</label>
                        </div>
                      </div>

                      <div className="field col-12 md:col-3">
                        <div className="p-float-label mt-3">
                          <Dropdown
                            options={pathReasons}
                            value={item.reason_to_join ?? null}
                            onChange={(event: DropdownChangeEvent) =>
                              onCommunityReasonToJoinChange(i, event.value)
                            }
                            placeholder="Select reason to join"
                            filter={true}
                            filterBy="name"
                            optionLabel="name"
                          />
                          <label>Reason to join</label>
                        </div>
                      </div>

                      <div className="field-checkbox col-12 md:col-3">
                        <Checkbox
                          checked={item.active}
                          onChange={(event: CheckboxChangeEvent) =>
                            onCommunityActiveChange(i, event.checked || false)
                          }
                          inputId={`community-active-${i}`}
                        />
                        <label htmlFor={`community-active-${i}`}>Active</label>
                      </div>

                      <div className="field col-12 md:col-12">
                        <div className="flex align-items-center gap-2">
                          {groupedCommunityOptions.map((group) => (
                            <div key={group.code} className="mb-2">
                              <label style={{ fontWeight: "bold" }}>
                                {group.label}
                              </label>
                              <Dropdown
                                value={item.selectedCommunityOptionId ?? null}
                                options={group.items}
                                onChange={(e: DropdownChangeEvent) => {
                                  onCommunityOptionSelect(i, e.value);
                                  setPendingCommunityNotes((prev) => ({
                                    ...prev,
                                    [i]: "",
                                  }));
                                }}
                                optionLabel="label"
                                optionValue="value"
                                placeholder={`Select option for ${group.label}`}
                                filter
                                filterBy="label"
                                style={{ minWidth: "250px" }}
                                disabled={group.items.length === 0}
                              />
                            </div>
                          ))}
                        </div>
                        {/* If an option is selected, show the editor immediately */}
                        {item.selectedCommunityOptionId && (
                          <div className="mt-2 mb-2">
                            <Editor
                              content={pendingCommunityNotes[i] || ""}
                              onChange={(value) =>
                                onPendingCommunityNoteChange(i, value)
                              }
                              modules={defaultEditorModules}
                              placeholder="Add note for this option"
                            />
                            <Button
                              icon="pi pi-plus"
                              label="Add option"
                              onClick={() => onAddCommunityOption(i)}
                              type="button"
                              className="mt-2"
                              disabled={!pendingCommunityNotes[i]?.trim()}
                            />
                          </div>
                        )}
                        {/* Render already added options */}
                        {item.community_options.map((opt, j) => (
                          <div
                            key={opt.community_option.id}
                            className="mt-3 mb-3 p-2 border-round surface-200"
                          >
                            <div className="flex align-items-center justify-content-between">
                              <div>
                                {opt.community_option.parent && (
                                  <div className="text-color-secondary mt-1">
                                    <span>
                                      Parent:{" "}
                                      <b>{opt.community_option.parent.name}</b>
                                    </span>
                                  </div>
                                )}
                                <strong>{opt.community_option.name}</strong>
                              </div>
                              <Button
                                icon="pi pi-trash"
                                className="p-button-danger p-button-sm"
                                onClick={() =>
                                  onRemoveCommunityOption(
                                    i,
                                    opt.community_option.id,
                                  )
                                }
                                type="button"
                                text
                              />
                            </div>
                            <Editor
                              content={opt.note}
                              onChange={(value) => {
                                setCommunities(
                                  communities.map((comm, commIdx) => {
                                    if (commIdx !== i) return comm;
                                    return {
                                      ...comm,
                                      community_options:
                                        comm.community_options.map((co, k) =>
                                          k === j ? { ...co, note: value } : co,
                                        ),
                                    };
                                  }),
                                );
                              }}
                              modules={defaultEditorModules}
                              placeholder="Add note for this option"
                            />
                          </div>
                        ))}

                        {uuid && item.id && (
                          <ContentBoxList
                            section={ContentBoxSectionEnum.COMMUNITY}
                            uuid={item.id}
                            contentBoxes={
                              communityContentBoxQueries?.[i]?.data?.data
                                .results || []
                            }
                            availableTags={tags || []}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Panel>

              {uuid && !isProfileManager() && !isProfileManagerTemp() && (
                <div>
                  {uuid && (
                    <Panel
                      header="Add new account visibility"
                      toggleable={true}
                      className="mb-3"
                    >
                      <div>
                        <Dropdown
                          value={selectedAccount}
                          options={accountsData?.data.results.map(
                            (account: AccountListItem) => ({
                              label: account.name,
                              value: account.id,
                            }),
                          )}
                          onChange={handleAccountChange}
                          placeholder="Select an account"
                          disabled={isLoadingAccounts}
                        />
                        <Dropdown
                          value={selectedVisibility}
                          options={visibilityOptions}
                          onChange={handleVisibilityChange}
                          placeholder="Select visibility"
                        />
                        <Button
                          label="Save"
                          onClick={handleAccountVisibilitySubmit}
                          type="button"
                        />
                      </div>
                    </Panel>
                  )}
                </div>
              )}
              {uuid &&
                !isProfileManager() &&
                !isProfileManagerTemp() &&
                newAccountVisibilities.length > 0 && (
                  <Panel
                    header="Account affiliation"
                    toggleable={true}
                    className="mb-3"
                  >
                    <div className="p-fluid">
                      <div className="grid formgrid">
                        {newAccountVisibilities.map((accountVisibility) => (
                          <div
                            key={accountVisibility.id}
                            className="field col-12"
                          >
                            <div className="p-float-label mt-3">
                              <Dropdown
                                value={accountVisibility.visibility}
                                options={accountVisibilityOptions}
                                onChange={(event: DropdownChangeEvent) => {
                                  onAccountVisibilityChange(
                                    accountVisibility.account,
                                    event.value,
                                  );
                                }}
                              />
                              <label>{accountVisibility.account.name}</label>
                              <Button
                                label="Remove"
                                icon="pi pi-times"
                                className="p-button-danger w-10rem"
                                onClick={() =>
                                  onDeleteAccountVisibility(
                                    accountVisibility.id,
                                  )
                                }
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Panel>
                )}

              <div className="flex gap-1 justify-content-end">
                <Button
                  type="button"
                  label="Cancel"
                  className="p-button-sm p-button-secondary"
                  text={true}
                  onClick={onCancel}
                  disabled={
                    createPeople.isLoading ||
                    updatePeople.isLoading ||
                    deletePeople.isLoading
                  }
                />

                {uuid && (
                  <Button
                    type="button"
                    label="Delete"
                    outlined={true}
                    className="p-button-sm p-button-danger"
                    onClick={onDelete}
                    disabled={
                      createPeople.isLoading ||
                      updatePeople.isLoading ||
                      deletePeople.isLoading
                    }
                  />
                )}
              </div>
            </TabPanel>

            <TabPanel header="Dynamic Fields">
              {fieldSections.map((section) => {
                const isGroupSection =
                  section.section.type === "group" &&
                  section.section.id !== "uncategorized" &&
                  section.section.id !== "all-fields";
                const hasNestedGroups = (section.section.fieldTypes || []).some(
                  (ft) => ft.type === DataTypeEnum.Group,
                );
                const sectionId = section.section.id;
                const isSectionOpen = openDynamicSectionIds.has(sectionId);
                const isSectionLoaded = loadedDynamicSectionIds.has(sectionId);
                const isSectionLoading =
                  loadingDynamicSectionIds.has(sectionId);
                const sectionFieldValues =
                  sectionFieldValuesById.get(sectionId) || [];

                return (
                  <Panel
                    key={sectionId}
                    header={section.section.name}
                    toggleable={true}
                    collapsed={!isSectionOpen}
                    className="mb-3"
                    onToggle={(event) => {
                      setOpenDynamicSectionIds((prev) => {
                        const next = new Set(prev);

                        if (event.value) {
                          next.delete(sectionId);
                        } else {
                          next.add(sectionId);
                        }

                        return next;
                      });

                      if (!event.value && !isSectionLoaded) {
                        setLoadingDynamicSectionIds((prev) => {
                          const next = new Set(prev);
                          next.add(sectionId);
                          return next;
                        });

                        window.setTimeout(() => {
                          setLoadedDynamicSectionIds((prev) => {
                            if (prev.has(sectionId)) {
                              return prev;
                            }

                            const next = new Set(prev);
                            next.add(sectionId);
                            return next;
                          });

                          window.setTimeout(() => {
                            setLoadingDynamicSectionIds((prev) => {
                              const next = new Set(prev);
                              next.delete(sectionId);
                              return next;
                            });
                          }, DYNAMIC_SECTION_LOADING_MIN_MS);
                        }, DYNAMIC_SECTION_LOAD_DELAY_MS);
                      }
                    }}
                  >
                    {isSectionLoading && (
                      <div className="flex align-items-center gap-3 py-4">
                        <ProgressSpinner
                          style={{ width: "24px", height: "24px" }}
                          strokeWidth="4"
                        />
                        <span className="text-600">
                          Loading {section.section.name}...
                        </span>
                      </div>
                    )}

                    {isSectionLoaded &&
                      (isGroupSection ? (
                        hasNestedGroups ? (
                          <NestedGroupRenderer
                            groupFieldType={section.section}
                            profileId={uuid || ""}
                            existingFieldValues={sectionFieldValues}
                            parentFieldValueId={null}
                            onRegisterSaver={(groupId, saver) => {
                              groupSaveRefs.current.set(groupId, saver);
                            }}
                            onSaveSuccess={() => {
                              appToastRef?.current?.show({
                                severity: "success",
                                summary: "Field saved successfully",
                                life: 3000,
                              });
                            }}
                            onSaveError={(error) => {
                              appToastRef?.current?.show({
                                severity: "error",
                                summary: "Field save error",
                                detail: error,
                                life: 5000,
                              });
                            }}
                          />
                        ) : (
                          <GroupFieldSection
                            groupFieldType={section.section}
                            childFieldTypes={section.fields}
                            profileId={uuid || ""}
                            existingFieldValues={sectionFieldValues}
                            parentFieldValueId={null}
                            onRegisterSaver={(groupId, saver) => {
                              groupSaveRefs.current.set(groupId, saver);
                            }}
                            onSaveSuccess={() => {
                              appToastRef?.current?.show({
                                severity: "success",
                                summary: "Field saved successfully",
                                life: 3000,
                              });
                            }}
                            onSaveError={(error) => {
                              appToastRef?.current?.show({
                                severity: "error",
                                summary: "Field save error",
                                detail: error,
                                life: 5000,
                              });
                            }}
                          />
                        )
                      ) : (
                        <div className="grid">
                          {section.fields.map((fieldType: FieldType) => {
                            const getFieldOptions = () => {
                              if (fieldType.type === "list") {
                                if (fieldType.validationRules?.list_items) {
                                  return fieldType.validationRules.list_items.map(
                                    (item: string) => ({
                                      label: item,
                                      value: item,
                                    }),
                                  );
                                }
                                if (fieldType.validationRules?.options) {
                                  return fieldType.validationRules.options.map(
                                    (option: any) => ({
                                      label: option.label || option,
                                      value: option.value || option,
                                    }),
                                  );
                                }
                              }
                              return [];
                            };

                            const fieldTypeValues =
                              profileFieldValuesByFieldTypeId.get(
                                fieldType.id,
                              ) || [];
                            const fieldValueWithChildren = fieldTypeValues.find(
                              (fieldValue) =>
                                profileFieldValuesByParentId.has(fieldValue.id),
                            );
                            const existingFieldValue =
                              fieldValueWithChildren ||
                              [...fieldTypeValues].sort(
                                (a, b) =>
                                  (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
                              )[0] ||
                              sectionFieldValues.find(
                                (fieldValue) =>
                                  fieldValue.fieldTypeId === fieldType.id,
                              );
                            const tagFieldValues = existingFieldValue
                              ? (
                                  profileFieldValuesByParentId.get(
                                    existingFieldValue.id,
                                  ) || []
                                ).filter(
                                  (fieldValue) =>
                                    fieldValue.type === "reference",
                                )
                              : [];
                            const childTags =
                              buildTagsWithFieldValueIds(tagFieldValues);

                            return (
                              <div
                                key={fieldType.id}
                                className="col-12 md:col-6"
                              >
                                <DynamicField
                                  fieldId={fieldType.id}
                                  fieldType={fieldType.type}
                                  label={fieldType.name}
                                  value={fieldManager.fields[fieldType.id]}
                                  onChange={(value) =>
                                    handleDynamicFieldChange(fieldType, value)
                                  }
                                  isRequired={fieldType.required}
                                  isSaving={fieldManager.isSavingField(
                                    fieldType.id,
                                  )}
                                  error={fieldManager.fieldErrors[fieldType.id]}
                                  isUnsaved={fieldManager.unsavedFields.has(
                                    fieldType.id,
                                  )}
                                  placeholder={
                                    fieldType.defaultValue || undefined
                                  }
                                  options={getFieldOptions()}
                                  referenceEntity={fieldType.referenceEntity}
                                  allowMultiple={fieldType.allowMultiple}
                                  validationRules={fieldType.validationRules}
                                  fieldValueId={existingFieldValue?.id}
                                />
                                {childTags.length > 0 &&
                                  existingFieldValue?.id && (
                                    <TagChips
                                      childTags={childTags}
                                      existingFieldValues={sectionFieldValues}
                                      profileId={uuid || ""}
                                      parentFieldValueId={
                                        existingFieldValue?.id
                                      }
                                      onSaveSuccess={() => {
                                        appToastRef?.current?.show({
                                          severity: "success",
                                          summary: "Tag updated successfully",
                                          life: 3000,
                                        });
                                      }}
                                      onSaveError={(error) => {
                                        appToastRef?.current?.show({
                                          severity: "error",
                                          summary: "Tag update failed",
                                          detail: error,
                                          life: 5000,
                                        });
                                      }}
                                    />
                                  )}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                  </Panel>
                );
              })}
            </TabPanel>
          </TabView>

          <div className="mt-4">
            <Button
              type="submit"
              label={isSubmitting ? "Submitting..." : "Save"}
              className="p-button-main p-button-sm"
              disabled={
                createPeople.isLoading ||
                updatePeople.isLoading ||
                deletePeople.isLoading ||
                isSubmitting
              }
            />
          </div>
        </form>
      </div>
      <FloatingSearchNavigation visible={!!searchTerm} />
    </div>
  );
}
